<template>
  <div>
    <!-- 悬浮的语音助手入口按钮 -->
    <div v-if="voiceButtonVisible && speechConfig.appid && speechConfig.secretid && speechConfig.secretKey" 
      class="floating-voice-button"
      :class="{ 'long-pressing': isLongPressing, 'dragging': floatingButton.isDragging }"
      :style="{ left: floatingButton.x + 'px', top: floatingButton.y + 'px' }" 
      @touchstart="handleTouchStart"
      @touchmove="handleTouchMove" 
      @touchend="handleTouchEnd" 
      @mousedown="handleMouseDown" 
      @mousemove="handleMouseMove"
      @mouseup="handleMouseUp" 
      @click="handleFloatingButtonClick">
      <div class="floating-button-inner">
        <svg class="microphone-icon" width="17" height="17" viewBox="0 0 24 24" fill="none">
          <rect x="9" y="4" width="6" height="10" rx="3" stroke="white" stroke-width="2" fill="none"/>
          <path d="M6 12C6 15.3137 8.68629 18 12 18C15.3137 18 18 15.3137 18 12" stroke="white" stroke-width="2" stroke-linecap="round"/>
          <line x1="12" y1="18" x2="12" y2="21" stroke="white" stroke-width="2" stroke-linecap="round"/>
          <line x1="9" y1="21" x2="15" y2="21" stroke="white" stroke-width="2" stroke-linecap="round"/>
        </svg>
      </div>
      <div class="floating-button-ripple"></div>
      <!-- 长按进度环 -->
      <div v-if="isLongPressing" class="long-press-progress">
        <svg class="progress-ring" width="44" height="44">
          <circle class="progress-ring-circle" 
            stroke="#fff" 
            stroke-width="2.2" 
            fill="transparent"
            r="20" 
            cx="22" 
            cy="22"
            :style="{ strokeDashoffset: longPressProgress }" />
        </svg>
      </div>
    </div>

    <!-- 角落提示：重新显示语音助手 - 小箭头 -->
    <div v-if="!voiceButtonVisible && speechConfig.appid && speechConfig.secretid && speechConfig.secretKey" 
      class="voice-corner-arrow"
      @click="showVoiceButton">
      <div class="arrow-icon">
        <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
          <path d="M8 2L4 6L8 10" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </div>
    </div>

    <!-- 隐藏确认对话框 -->
    <van-dialog
      v-model="hideConfirmVisible"
      title="隐藏语音助手"
      show-cancel-button
      confirm-button-text="确定隐藏"
      cancel-button-text="取消"
      @confirm="confirmHideVoiceButton"
      @cancel="cancelHideVoiceButton">
      <div class="hide-confirm-content">
        <p>确定要隐藏语音助手吗？</p>
        <div class="hint-box">
          <div class="hint-title">💡 点击右下角小箭头可重新显示</div>
        </div>
        <van-checkbox v-model="dontShowHideHint">不再显示此提示</van-checkbox>
      </div>
    </van-dialog>

    <!-- 遮罩层（对话模式） -->
    <div v-if="voiceModalVisible && conversationMode" 
      class="voice-modal-overlay"
      @click="closeVoiceModal"></div>

    <!-- 语音助手小弹窗 / 全屏对话模式 -->
    <div v-if="voiceModalVisible" 
      class="voice-assistant-small-modal" 
      :class="{ 'fullscreen-mode': conversationMode }"
      :style="conversationMode ? {} : modalPosition">
      <!-- 标题栏 -->
      <div class="modal-header">
        <!-- 左侧按钮组 -->
        <div class="modal-header-left">
          <div v-if="conversationMode && conversationHistory.length > 0" class="modal-clear-btn" @click="clearConversation">
            <van-icon name="delete-o" size="14" color="#fff" />
          </div>
          <div class="modal-settings-btn" 
            :class="{ 'wake-word-active': wakeWordEnabled, 'wake-word-listening': isWakeWordListening }"
            @click.stop="toggleWakeWord" 
            :title="wakeWordEnabled ? '关闭唤醒词' : '开启唤醒词'">
            <!-- 耳朵形状图标（顺时针旋转90度） -->
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="transform: rotate(90deg);">
              <path d="M12 2C8.13 2 5 5.13 5 9c0 5 3 7 3 9h8c0-2 3-4 3-9 0-3.87-3.13-7-7-7z"/>
              <path d="M12 2v7"/>
              <path d="M9 9c0-1.66 1.34-3 3-3s3 1.34 3 3"/>
            </svg>
          </div>
        </div>
        
        <!-- 中间标题 -->
        <div class="modal-title">智能语音助手</div>
        
        <!-- 右侧按钮组 -->
        <div class="modal-header-right">
          <div class="modal-close-btn" @click="closeVoiceModal">
            <van-icon name="cross" size="12" color="#fff" />
          </div>
        </div>
      </div>

      <!-- 对话内容区域（全屏模式） -->
      <div v-if="conversationMode" class="conversation-content-area">
        <div class="conversation-messages" ref="conversationMessages">
          <!-- 空状态提示 -->
          <div v-if="conversationHistory.length === 0" class="empty-conversation">
            <div class="empty-icon">💬</div>
            <div class="empty-text">开始对话吧</div>
            <div class="empty-hint">按住下方按钮说话</div>
          </div>
          
          <!-- 历史对话消息 -->
          <div v-for="(msg, index) in conversationHistory" :key="index" 
            class="message-item" 
            :class="msg.role">
            <div class="message-bubble">
              <div class="message-text">{{ msg.content }}</div>
            </div>
          </div>
        </div>
      </div>

      <!-- 输入区域（固定在底部） -->
      <div class="input-area" :class="{ 'fullscreen-input': conversationMode }">
        <!-- 实时语音输入显示（对话模式下） -->
        <div v-if="conversationMode && recognitionText" class="realtime-input-display">
          <div class="realtime-input-box">
            <div class="realtime-input-text">{{ recognitionText }}</div>
          </div>
        </div>

        <!-- 处理状态提示（对话模式下） -->
        <div v-if="conversationMode && (isProcessing || isApiProcessing)" class="processing-status">
          <span v-if="isProcessing">正在识别中...</span>
          <span v-if="isApiProcessing">AI正在分析中...</span>
        </div>

        <!-- 语音按钮容器 -->
        <div class="voice-button-container-small">
          <button class="voice-button-small" 
            :class="{ 
              'recording': isRecording || isClickRecording, 
              'processing': isProcessing,
              'api-processing': isApiProcessing,
              'cancel-gesture': isCancelGesture
            }"
            @touchstart.prevent="handleVoiceTouchStart" 
            @touchmove.prevent="handleVoiceTouchMove"
            @touchend.prevent="handleVoiceTouchEnd" 
            @mousedown.prevent="handleVoiceMouseDown"
            @mouseup.prevent="handleVoiceMouseUp" 
            @mouseleave="handleVoiceMouseLeave"
            @click.prevent="handleVoiceClick"
            :disabled="isProcessing || isApiProcessing || isInitializing">
            {{ currentButtonText }}
          </button>

          <!-- 动画效果容器 -->
          <div v-if="isRecording" class="voice-animation-small">
            <div class="pulse-circle-small"></div>
            <div class="wave-lines-small">
              <span v-for="i in 3" :key="i" class="wave-line-small" :style="{ '--i': i }"></span>
            </div>
          </div>
        </div>

        <!-- 状态提示（普通模式） -->
        <div v-if="!conversationMode && statusText" class="status-text-small">{{ statusText }}</div>
        
        <!-- 唤醒词监听状态提示 -->
        <div v-if="!conversationMode && isWakeWordListening && !isSpeaking && !isRecording" class="wake-word-status">
          <div class="wake-word-indicator">
            <div class="listening-dot"></div>
            <span>说出“你好小助手”开始语音指令</span>
          </div>
        </div>

        <!-- 识别结果显示区域（普通模式） -->
        <div v-if="!conversationMode" class="recognition-result-small">
          <div v-if="recognitionText" class="result-text-small">
            {{ recognitionText }}
          </div>
          <div v-if="isProcessing" class="processing-text-small">
            正在识别中...
          </div>
          <div v-if="isApiProcessing" class="processing-text-small">
            AI正在分析中...
          </div>
          <div v-if="!recognitionText && !isProcessing && !isApiProcessing && !statusText" class="placeholder-text-small">
            <div class="scrolling-examples">
              <div class="example-text" :key="currentExampleIndex">
                {{ exampleTexts[currentExampleIndex] }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

</template>

<script>
// 导入腾讯云语音SDK相关文件
import SpeechRecognizer from '@/utils/speech/speechrecognizer.js';
import WebRecorder from '@/utils/speech/webrecorder.js';
// 导入混元AI接口
import { postHunyuan } from '@/api/commonApi.js';
import { mapGetters } from 'vuex';

export default {
  name: 'VoiceAssistant',
  props: {
    // 是否自动调用AI接口
    autoCallAI: {
      type: Boolean,
      default: true
    },
    // 初始位置
    initialPosition: {
      type: Object,
      default: () => ({
        x: null,
        y: null
      })
    }
  },
  data() {
    return {
      // 弹窗控制
      voiceModalVisible: false,
      microphonePermissionGranted: false,
      showVoice:false,

      // 悬浮按钮相关
      floatingButton: {
        x: 7.5, // 初始x位置
        y: 100, // 初始y位置
        isDragging: false,
        startX: 0,
        startY: 0,
        initialX: 0,
        initialY: 0
      },

      // 隐藏/显示控制
      voiceButtonVisible: true, // 语音按钮是否可见
      hideConfirmVisible: false, // 隐藏确认对话框是否显示
      dontShowHideHint: false, // 不再显示隐藏提示
      isLongPressing: false, // 是否正在长按
      longPressTimer: null, // 长按计时器
      longPressProgress: 125.66, // 长按进度（SVG circle周长：2 * π * 20）
      longPressDuration: 1500, // 长按时长(ms)
      
      // 滚动隐藏控制
      isScrolling: false, // 是否正在滚动
      scrollTimer: null, // 滚动计时器
      isButtonHiddenByScroll: false, // 按钮是否因滚动而隐藏
      scrollContainers: [], // 存储所有可滚动容器的引用
      scrollObserver: null, // MutationObserver 实例
      observerTimer: null, // Observer 防抖计时器

      // 语音助手相关数据
      isRecording: false,
      isInitializing: false, // 初始化状态（从点击到开始识别）
      isProcessing: false,
      isApiProcessing: false, // API接口调用状态（腾讯云语音识别 + 混元AI）
      
      // 智能交互检测
      isClickRecording: false, // 点击模式下的录音状态
      pressStartTime: 0, // 按下开始时间
      longPressThreshold: 200, // 长按阈值(ms)，超过此时间认为是长按
      isLongPress: false, // 是否为长按操作
      
      // 手势控制
      touchStartY: 0,
      touchStartX: 0,
      isCancelGesture: false,
      cancelThreshold: 50, // 滑动取消的阈值(px)

      // 腾讯云语音识别相关
      recorder: null,
      speechRecognizer: null,
      isCanSendData: false,
      isCanStop: false,

      // 语音识别配置
      speechConfig: {
        // 鉴权函数 - 需要根据实际情况实现
        signCallback: this.signCallback,
        appid: '',
        hotword_id: "556aab3bad8e11f0b984446a2e8b4d02",
        // 用户参数 - 从七彩石配置获取
        secretid: '',
        secretKey: '',
        region: 'ap-guangzhou', // 地域
        engineType: '16k_zh_large', // 引擎类型
        voice_format: 1,
        needvad: 1,
        filter_dirty: 1,
        filter_modal: 1,
        filter_punc: 1,
        convert_num_mode: 1,
        word_info: 2
      },

      // 界面状态
      buttonText: '按住说话',
      statusText: '',
      recognitionText: '',
      resultText: '', // 用于累积识别结果

      // 计时器
      recordingTimer: null,
      recordingStartTime: 0,
      maxRecordingTime: 60000, // 最大录音时间60秒
      recordingTimeoutTimer: null, // 录音超时计时器

      // 滚动示例文本
      exampleTexts: [
        '搜索固资号以F755结尾的工单',
        '搜索服务器故障维修单',
        '更多功能正在接入中，敬请期待',
      ],
      currentExampleIndex: 0,
      exampleScrollTimer: null,
      
      // 对话模式
      conversationMode: false, // 是否为对话模式
      conversationHistory: [], // 对话历史记录（用于展示，最多3轮）
      fullConversationHistory: [], // 完整对话历史（用于保存，最多3轮）
      
      // 唤醒词监听相关
      wakeWordEnabled: false, // 是否启用唤醒词监听
      isWakeWordListening: false, // 是否正在监听唤醒词
      wakeWordRecognizer: null, // 腾讯云语音识别实例（唤醒词专用）
      wakeWordRecorder: null, // 录音器实例（唤醒词专用）
      wakeWords: ['你好小助手', '小助手', '嘿小助手'], // 支持的唤醒词列表
      silenceTimer: null, // 静默计时器
      silenceTimeout: 3000, // 静默超时时间（5秒）
      lastSpeechTime: 0, // 最后一次说话时间
      wakeWordRetryCount: 0, // 唤醒词监听重试次数
      maxWakeWordRetry: 5, // 最大重试次数
      wakeWordReconnectTimer: null, // 唤醒词重连定时器
      wakeWordHeartbeatTimer: null, // 唤醒词心跳定时器
      wakeWordHeartbeatInterval: 10000, // 心跳间隔（10秒）
      isWakeWordCanSendData: false, // 是否可以发送唤醒词数据
      
      // 唤醒词防抖相关
      wakeWordDebounceTimer: null, // 唤醒词防抖计时器
      wakeWordDebounceDelay: 2000, // 防抖延迟时间（3秒）
      pendingWakeText: '', // 待处理的唤醒文本
      pendingUserQuery: '', // 待处理的用户需求
      isWakeWordTriggered: false, // 唤醒词是否已触发（防止重复触发）
      
      // TTS语音播报相关
      ttsEnabled: true, // 是否启用TTS语音播报（默认启用）
      isSpeaking: false, // 是否正在播报语音
      currentUtterance: null, // 当前的语音合成实例
    }
  },
  computed: {
    ...mapGetters(['getConfigItem']),
    
    // 动态按钮文本
    currentButtonText() {
      if (this.isApiProcessing) {
        return 'AI处理中...';
      }
      if (this.isProcessing) {
        return '识别中...';
      }
      if (this.isInitializing) {
        return '请稍等...';
      }
      if (this.isCancelGesture) {
        return '松开取消';
      }
      if (this.isRecording) {
        return this.isLongPress ? '松开结束' : '录音中...';
      }
      if (this.isClickRecording) {
        return '点击结束';
      }
      // 移除麦克风权限提示，因为打开弹窗时已自动申请
      return '长按或点击说话';
    },
    
    // 检查语音配置是否完整
    // 计算小弹窗的位置
    modalPosition() {
      const buttonSize = 32;
      // 对话模式下增加宽度
      const modalWidth = this.conversationMode ? 280 : 220;
      // 对话模式下增加高度
      const modalHeight = this.conversationMode ? 320 : 185;
      const gap = 25; // 进一步增加间距
      const padding = 15;

      const screenWidth = window.innerWidth;
      const screenHeight = window.innerHeight;

      // 按钮的边界（添加额外的安全区域）
      const safeZone = 10; // 额外的安全区域
      const buttonLeft = this.floatingButton.x - safeZone;
      const buttonRight = this.floatingButton.x + buttonSize + safeZone;
      const buttonTop = this.floatingButton.y - safeZone;
      const buttonBottom = this.floatingButton.y + buttonSize + safeZone;

      let left, top;
      let positioned = false;
      
      // 屏幕边界的安全边距
      const screenPadding = 10;

      // 检查两个矩形是否重叠的函数
      const isOverlapping = (modalLeft, modalTop, modalRight, modalBottom) => {
        return !(modalRight < buttonLeft ||
          modalLeft > buttonRight ||
          modalBottom < buttonTop ||
          modalTop > buttonBottom);
      };

      // 1. 尝试显示在按钮左侧
      if (!positioned) {
        left = this.floatingButton.x - modalWidth - gap;
        top = this.floatingButton.y + (buttonSize / 2) - (modalHeight / 2);

        // 边界检查（确保不超出屏幕）
        if (left >= screenPadding) {
          // 垂直位置调整
          if (top < screenPadding) top = screenPadding;
          if (top + modalHeight > screenHeight - screenPadding) {
            top = screenHeight - modalHeight - screenPadding;
          }

          // 碰撞检测
          if (!isOverlapping(left, top, left + modalWidth, top + modalHeight)) {
            positioned = true;
          }
        }
      }
      // 2. 尝试显示在按钮右侧
      if (!positioned) {
        left = this.floatingButton.x + buttonSize + gap;
        top = this.floatingButton.y + (buttonSize / 2) - (modalHeight / 2);

        // 边界检查（确保不超出屏幕）
        if (left + modalWidth <= screenWidth - screenPadding) {
          // 垂直位置调整
          if (top < screenPadding) top = screenPadding;
          if (top + modalHeight > screenHeight - screenPadding) {
            top = screenHeight - modalHeight - screenPadding;
          }

          // 碰撞检测
          if (!isOverlapping(left, top, left + modalWidth, top + modalHeight)) {
            positioned = true;
          }
        }
      }
      // 3. 尝试显示在按钮上方
      if (!positioned) {
        left = this.floatingButton.x + (buttonSize / 2) - (modalWidth / 2);
        top = this.floatingButton.y - modalHeight - gap;
        
        // 水平位置调整（确保不超出屏幕）
        if (left < screenPadding) left = screenPadding;
        if (left + modalWidth > screenWidth - screenPadding) {
          left = screenWidth - modalWidth - screenPadding;
        }
        
        // 边界检查
        if (top >= screenPadding) {
          // 碰撞检测
          if (!isOverlapping(left, top, left + modalWidth, top + modalHeight)) {
            positioned = true;
          }
        }
      }
      // 4. 最后尝试显示在按钮下方
      if (!positioned) {
        left = this.floatingButton.x + (buttonSize / 2) - (modalWidth / 2);
        top = this.floatingButton.y + buttonSize + gap;
        
        // 水平位置调整（确保不超出屏幕）
        if (left < screenPadding) left = screenPadding;
        if (left + modalWidth > screenWidth - screenPadding) {
          left = screenWidth - modalWidth - screenPadding;
        }
        
        // 垂直位置调整（确保不超出屏幕）
        if (top + modalHeight > screenHeight - screenPadding) {
          top = screenHeight - modalHeight - screenPadding;
        }
        if (top < screenPadding) top = screenPadding;

        positioned = true; // 强制使用这个位置作为最后的选择
      }

      return {
        position: 'fixed',
        left: `${left}px`,
        top: `${top}px`,
        width: `${modalWidth}px`,
        zIndex: 1001
      };
    }
  },
  watch: {
    // 监听语音识别文本变化，在对话模式下自动滚动
    recognitionText(newVal) {
      if (this.conversationMode && newVal) {
        this.$nextTick(() => {
          this.scrollToBottom();
        });
      }
    }
  },
  mounted() {
    this.initUsers();
    // 初始化语音识别配置
    this.initSpeechConfig();
    // 初始化悬浮按钮位置
    this.initFloatingButtonPosition();
    // 启动示例文本滚动
    this.startExampleScroll();
    // 恢复用户的隐藏/显示选择
    this.restoreButtonVisibility();
    // 加载对话历史
    this.loadConversationFromSession();
    
    // 恢复唤醒词设置
    try {
      const savedWakeWordEnabled = localStorage.getItem('wakeWordEnabled');
      if (savedWakeWordEnabled !== null) {
        this.wakeWordEnabled = savedWakeWordEnabled === 'true';
        
        // 如果用户之前开启了唤醒词，自动申请麦克风权限并启动监听
        if (this.wakeWordEnabled) {
          this.$nextTick(async () => {
            // 先申请麦克风权限
            if (!this.microphonePermissionGranted) {
              console.log('[VoiceAssistant] 唤醒词已开启，自动申请麦克风权限');
              await this.requestMicrophonePermission();
            }
            
            // 如果权限获取成功，启动唤醒词监听
            if (this.microphonePermissionGranted) {
              this.startWakeWordListening();
            } else {
              console.warn('[VoiceAssistant] 麦克风权限未获取，无法启动唤醒词监听');
              // 权限未获取，关闭唤醒词功能
              this.wakeWordEnabled = false;
              localStorage.setItem('wakeWordEnabled', 'false');
            }
          });
        }
      }
    } catch (e) {
      console.error('[VoiceAssistant] 恢复唤醒词设置失败:', e);
    }

    // 监听窗口大小变化
    window.addEventListener('resize', this.handleWindowResize);
    // 监听页面滚动
    window.addEventListener('scroll', this.handlePageScroll, { passive: true });
    
    // 监听所有可滚动容器的滚动事件（包括 van-list）
    this.$nextTick(() => {
      this.attachScrollListeners();
    });
  },
  beforeDestroy() {
    // 停止唤醒词监听
    this.stopWakeWordListening();
    
    // 清理唤醒词防抖计时器
    if (this.wakeWordDebounceTimer) {
      clearTimeout(this.wakeWordDebounceTimer);
      this.wakeWordDebounceTimer = null;
    }
    
    // 清理语音录制相关资源
    this.resetRecording();
    
    // 停止语音播放
    if (window && window.speechSynthesis && typeof window.speechSynthesis.cancel === 'function') {
      window.speechSynthesis.cancel();
      console.log('[VoiceAssistant] 已停止语音播放');
    }
    this.isApiProcessing = false; // 重置API处理状态
    
    // 清理示例文本滚动定时器
    this.stopExampleScroll();

    // 清理长按检测
    this.cancelLongPressDetection();
    
    // 清理滚动监听
    window.removeEventListener('scroll', this.handlePageScroll);
    this.detachScrollListeners();
    if (this.scrollTimer) {
      clearTimeout(this.scrollTimer);
    }

    // 清理事件监听
    window.removeEventListener('resize', this.handleWindowResize);
    document.removeEventListener('mousemove', this.handleMouseMove);
    document.removeEventListener('mouseup', this.handleMouseUp);
  },
  methods: {
    // ==================== 唤醒词监听相关方法 ====================
    
    // 切换唤醒词功能
    async toggleWakeWord() {
      this.wakeWordEnabled = !this.wakeWordEnabled;
      
      // 保存用户设置
      try {
        localStorage.setItem('wakeWordEnabled', this.wakeWordEnabled);
      } catch (e) {
        console.error('[VoiceAssistant] 保存唤醒词设置失败:', e);
      }
      
      if (this.wakeWordEnabled) {
        // 先申请麦克风权限
        if (!this.microphonePermissionGranted) {
          console.log('[VoiceAssistant] 开启唤醒词，申请麦克风权限');
          await this.requestMicrophonePermission();
        }
        
        // 检查权限是否获取成功
        if (!this.microphonePermissionGranted) {
          console.warn('[VoiceAssistant] 麦克风权限未获取，无法启动唤醒词监听');
          
          // 权限未获取，恢复开关状态
          this.wakeWordEnabled = false;
          localStorage.setItem('wakeWordEnabled', 'false');
          
          this.$toast({
            message: '需要麦克风权限才能使用唤醒词功能',
            type: 'fail',
            duration: 3000
          });
          
          return;
        }
        
        // 启用唤醒词监听
        this.$toast({
          message: '唤醒词监听已开启，说"你好小助手"即可唤醒',
          type: 'success',
          duration: 3000
        });
        
        // 启动监听
        this.startWakeWordListening();
        
        // 上报启用事件
        this.reportWakeWordEvent('enable');
      } else {
        // 停止唤醒词监听
        this.$toast({
          message: '唤醒词监听已关闭',
          type: 'info',
          duration: 2000
        });
        
        // 用户手动关闭，播放提示音
        this.stopWakeWordListening(true);
        
        // 上报停用事件
        this.reportWakeWordEvent('disable');
      }
    },
    
    // 开始唤醒词监听（使用腾讯云SDK）
    async startWakeWordListening() {
      if (this.isWakeWordListening) {
        console.log('[VoiceAssistant] 唤醒词监听已在运行');
        return;
      }
      
      try {
        console.log('[VoiceAssistant] 开始唤醒词监听（使用腾讯云SDK）');
        
        this.isWakeWordListening = true;
        this.isWakeWordCanSendData = false;
        
        // 初始化唤醒词录音器
        this.wakeWordRecorder = new WebRecorder();
        
        // 设置录音数据回调
        this.wakeWordRecorder.OnReceivedData = (res) => {
          if (this.isWakeWordCanSendData && this.wakeWordRecognizer) {
            try {
              this.wakeWordRecognizer.write(res);
            } catch (error) {
              console.error('[VoiceAssistant] 发送唤醒词数据失败:', error);
              this.handleWakeWordError(error);
            }
          }
        };
        
        // 录音失败回调
        this.wakeWordRecorder.OnError = (err) => {
          console.error('[VoiceAssistant] 唤醒词录音失败:', err);
          this.handleWakeWordError(err);
        };
        
        // 开始录音
        this.wakeWordRecorder.start();
        
        // 初始化唤醒词语音识别器
        await this.initWakeWordRecognizer();
        
        console.log('[VoiceAssistant] 唤醒词监听已启动（腾讯云SDK）');
        
      } catch (error) {
        console.error('[VoiceAssistant] 启动唤醒词监听失败:', error);
        this.isWakeWordListening = false;
        
        this.$toast({
          message: error.message || '启动唤醒词监听失败',
          type: 'fail',
          duration: 2000
        });
        
        // 上报启动失败事件
        this.reportWakeWordEvent('start_error', { error: error.message });
      }
    },
    
    // 初始化唤醒词识别器
    async initWakeWordRecognizer() {
      try {
        // 创建新的识别器实例
        this.wakeWordRecognizer = new SpeechRecognizer(this.speechConfig);
        
        // 设置识别回调
        this.setupWakeWordRecognitionCallbacks();
        
        // 启动识别连接
        await this.wakeWordRecognizer.start();
        
        // 启动心跳定时器（每10秒发送一次静音数据，防止超时断连）
        this.startWakeWordHeartbeat();
        
        console.log('[VoiceAssistant] 唤醒词识别器初始化成功');
        
      } catch (error) {
        console.error('[VoiceAssistant] 初始化唤醒词识别器失败:', error);
        throw error;
      }
    },
    
    // 设置唤醒词识别回调
    setupWakeWordRecognitionCallbacks() {
      if (!this.wakeWordRecognizer) return;
      
      // 开始识别
      this.wakeWordRecognizer.OnRecognitionStart = (res) => {
        console.log('[VoiceAssistant] 唤醒词识别开始', res);
        this.isWakeWordCanSendData = true;
        this.wakeWordRetryCount = 0; // 重置重试计数
        this.reportWakeWordEvent('start_success');
      };
      
      // 一句话开始
      this.wakeWordRecognizer.OnSentenceBegin = (res) => {
        console.log('[VoiceAssistant] 唤醒词句子开始', res);
        // 在唤醒词模式下，不清空之前的识别内容
        // 这样用户停顿后继续说话时，内容会继续拼接
      };
      
      // 识别结果变化
      this.wakeWordRecognizer.OnRecognitionResultChange = (res) => {
        if (res?.voice_text_str) {
          const transcript = this.replaceRecognitionText(res.voice_text_str.trim());
          console.log('[VoiceAssistant] 唤醒词识别中:', transcript);
          
          // 先检查唤醒词，让 checkWakeWord 来决定是否显示
          this.checkWakeWord(transcript);
        }
      };
      
      // 一句话结束
      this.wakeWordRecognizer.OnSentenceEnd = (res) => {
        if (res?.voice_text_str) {
          const transcript = res.voice_text_str.trim();
          console.log('[VoiceAssistant] 唤醒词句子结束:', transcript);
          
          // 先检查唤醒词，让 checkWakeWord 来决定是否显示
          this.checkWakeWord(transcript);
        }
      };
      
      // 识别完成（不应该触发，因为是持续监听）
      this.wakeWordRecognizer.OnRecognitionComplete = (res) => {
        console.log('[VoiceAssistant] 唤醒词识别完成（异常）', res);
        // 自动重连
        if (this.isWakeWordListening && !this.voiceModalVisible) {
          console.log('[VoiceAssistant] 唤醒词识别意外结束，准备重连');
          this.reconnectWakeWordListening();
        }
      };
      
      // 识别错误
      this.wakeWordRecognizer.OnError = (error) => {
        console.error('[VoiceAssistant] 唤醒词识别错误:', error);
        
        // 上报错误
        this.reportWakeWordEvent('recognition_error', { 
          error: error.message || error.toString() 
        });
        
        // WebSocket断开或超时，触发重连
        if (this.isWakeWordListening && !this.voiceModalVisible) {
          console.log('[VoiceAssistant] 唤醒词识别出错，准备重连');
          this.reconnectWakeWordListening();
        }
      };
    },
    
    // 检查是否包含唤醒词
    checkWakeWord(transcript) {
      if (!transcript) return;
      
      // 如果唤醒词已经触发过，直接进入累积用户需求的逻辑
      if (this.isWakeWordTriggered) {
        console.log('[VoiceAssistant] 唤醒词已触发，继续累积用户需求:', transcript);
        
        // 查找最后一个唤醒词的位置
        let cleanedTranscript = transcript;
        let lastWakeWordIndex = -1;
        let lastWakeWordLength = 0;
        
        for (const word of this.wakeWords) {
          const index = transcript.lastIndexOf(word);
          if (index > lastWakeWordIndex) {
            lastWakeWordIndex = index;
            lastWakeWordLength = word.length;
          }
        }
        
        // 如果找到唤醒词，提取其后的内容
        if (lastWakeWordIndex !== -1) {
          cleanedTranscript = transcript.substring(lastWakeWordIndex + lastWakeWordLength);
          
          // 去除开头的标点符号和空格
          const newQuery = cleanedTranscript.replace(/^[，。！？、,.\\s]+/, '').trim();
          
          if (newQuery) {
            // 实时显示用户输入
            this.recognitionText = this.replaceRecognitionText(newQuery);
            
            // 更新用户需求
            this.pendingUserQuery = newQuery;
            this.pendingWakeText = newQuery;
            
            // 清除并重置3秒防抖计时器，等待用户说完
            if (this.wakeWordDebounceTimer) {
              clearTimeout(this.wakeWordDebounceTimer);
            }
            this.wakeWordDebounceTimer = setTimeout(() => {
              console.log('[VoiceAssistant] 用户停止说话3秒，开始处理');
              this.executeWakeWordCommand();
            }, 4000);
          }
        } else {
          // 没有找到唤醒词，说明这是唤醒后的用户输入
          // 直接显示整个 transcript 作为用户输入
          const userInput = transcript.trim();
          if (userInput) {
            this.recognitionText = this.replaceRecognitionText(userInput);
            this.pendingUserQuery = userInput;
            this.pendingWakeText = userInput;
            
            // 清除并重置3秒防抖计时器，等待用户说完
            if (this.wakeWordDebounceTimer) {
              clearTimeout(this.wakeWordDebounceTimer);
            }
            this.wakeWordDebounceTimer = setTimeout(() => {
              console.log('[VoiceAssistant] 用户停止说话3秒，开始处理');
              this.executeWakeWordCommand();
            }, 4000);
          }
        }
        
        return;
      }
      
      // 检查是否包含唤醒词（首次检测）
      const hasWakeWord = this.wakeWords.some(word => transcript.includes(word));
      
      if (hasWakeWord) {
        console.log('[VoiceAssistant] 检测到唤醒词:', transcript);
        
        // 标记唤醒词已触发
        this.isWakeWordTriggered = true;
        this.isRecording = true;
        this.statusText === '我在听...'
        
        // 清空之前的所有输入（唤醒词触发前的内容）
        console.log('[VoiceAssistant] 清空唤醒词触发前的所有输入');
        this.recognitionText = '';
        this.pendingWakeText = '';
        this.pendingUserQuery = '';
        
        // 清除之前的防抖计时器
        if (this.wakeWordDebounceTimer) {
          clearTimeout(this.wakeWordDebounceTimer);
          this.wakeWordDebounceTimer = null;
        }
        
        // 播放唤醒提示音 (使用稍高频率表示唤醒成功)
        this.playListeningSound(1000, 0.1, 0.35);
        
        // 提取唤醒词后面的内容作为用户需求
        let userQuery = '';
        for (const word of this.wakeWords) {
          const index = transcript.indexOf(word);
          if (index !== -1) {
            // 获取唤醒词后面的内容（包括标点符号后的内容）
            const afterWakeWord = transcript.substring(index + word.length);
            // 去除开头的标点符号和空格
            userQuery = afterWakeWord.replace(/^[，。！？、,.\s]+/, '').trim();
            break;
          }
        }
        
        // 保存待处理的数据（只保存唤醒词后的内容）
        this.pendingWakeText = userQuery || '已唤醒'; // 使用用户需求作为待处理文本
        this.pendingUserQuery = userQuery;
        
        // 打开语音助手弹窗（如果还没打开）
        if (!this.voiceModalVisible) {
          this.showVoiceModal();
        }
        
        // 实时显示唤醒词后的内容（不包括唤醒词）
        // 如果有用户需求，显示需求；否则显示提示
        if (userQuery) {
          this.recognitionText = this.replaceRecognitionText(userQuery);
        } else {
          this.recognitionText = '已唤醒，请继续说话...';
        }
        
        // 启动3秒防抖计时器，等待用户说完
        console.log('[VoiceAssistant] 启动3秒防抖计时器，等待用户说完');
        this.wakeWordDebounceTimer = setTimeout(() => {
          console.log('[VoiceAssistant] 用户停止说话3秒，开始处理');
          this.executeWakeWordCommand();
        }, 4000); // 固定3秒
        
        // 上报唤醒事件
        this.reportWakeWordEvent('wake_detected', { text: transcript, userQuery });
      } else {
        // 没有检测到唤醒词，且唤醒词还未触发
        // 不显示任何内容，保持界面清空
        // 这样可以避免唤醒词之前的内容被显示
      }
    },
    
    
    // 执行唤醒词命令
    executeWakeWordCommand() {
      if (!this.pendingWakeText) return;
      
      console.log('[VoiceAssistant] 执行唤醒词命令');
      console.log('[VoiceAssistant] 唤醒文本:', this.pendingWakeText);
      console.log('[VoiceAssistant] 用户需求:', this.pendingUserQuery);
      
      // 如果有用户需求，直接处理
      if (this.pendingUserQuery && this.pendingUserQuery.length > 0) {
        // 停止唤醒词监听
        this.stopWakeWordListening();
        
        // 震动反馈
        if (navigator.vibrate) {
          navigator.vibrate([50, 100, 50]);
        }
        
        // 显示提示
        this.$toast({
          message: '已识别您的需求',
          type: 'success',
          duration: 1500
        });
        
        // 直接调用AI接口处理用户需求
        if (this.autoCallAI) {
          this.callHunyuanAPI(this.pendingUserQuery);
        }
        
        // 重置触发标志（有用户需求时，流程已结束）
        this.isWakeWordTriggered = false;
      } else {
        // 没有用户需求，触发普通唤醒流程
        // 注意：不在这里重置 isWakeWordTriggered，因为用户录音过程中不应该再次触发唤醒
        this.onWakeWordDetected(this.pendingWakeText, this.pendingUserQuery);
      }
      
      // 清空待处理数据
      this.pendingWakeText = '';
      this.pendingUserQuery = '';
      this.wakeWordDebounceTimer = null;
    },
    
    // 启动唤醒词心跳（防止超时断连）
    startWakeWordHeartbeat() {
      // 清除旧的心跳定时器
      this.stopWakeWordHeartbeat();
      
      // 每10秒发送一次心跳（发送静音数据）
      this.wakeWordHeartbeatTimer = setInterval(() => {
        if (this.isWakeWordCanSendData && this.wakeWordRecognizer) {
          try {
            // 发送一个空的音频数据作为心跳
            // 注意：这里发送的是静音数据，不会影响识别结果
            const silenceData = new ArrayBuffer(640); // 20ms的静音数据
            this.wakeWordRecognizer.write(silenceData);
            console.log('[VoiceAssistant] 发送唤醒词心跳');
          } catch (error) {
            console.error('[VoiceAssistant] 发送唤醒词心跳失败:', error);
          }
        }
      }, this.wakeWordHeartbeatInterval);
      
      console.log('[VoiceAssistant] 唤醒词心跳已启动');
    },
    
    // 停止唤醒词心跳
    stopWakeWordHeartbeat() {
      if (this.wakeWordHeartbeatTimer) {
        clearInterval(this.wakeWordHeartbeatTimer);
        this.wakeWordHeartbeatTimer = null;
        console.log('[VoiceAssistant] 唤醒词心跳已停止');
      }
    },
    
    // 重连唤醒词监听
    async reconnectWakeWordListening() {
      // 清除旧的重连定时器
      if (this.wakeWordReconnectTimer) {
        clearTimeout(this.wakeWordReconnectTimer);
        this.wakeWordReconnectTimer = null;
      }
      
      // 增加重试计数
      this.wakeWordRetryCount++;
      
      console.log(`[VoiceAssistant] 唤醒词重连 (${this.wakeWordRetryCount}/${this.maxWakeWordRetry})`);
      
      // 检查是否超过最大重试次数
      if (this.wakeWordRetryCount > this.maxWakeWordRetry) {
        console.error('[VoiceAssistant] 唤醒词重连次数已达上限，停止监听');
        
        this.wakeWordEnabled = false;
        this.stopWakeWordListening();
        
        this.$toast({
          message: '唤醒词监听连接不稳定，已自动关闭',
          type: 'fail',
          duration: 3000
        });
        
        // 上报重连失败
        this.reportWakeWordEvent('reconnect_failed', { 
          retryCount: this.wakeWordRetryCount 
        });
        
        return;
      }
      
      // 先清理旧的连接
      this.cleanupWakeWordConnection();
      
      // 延迟重连（指数退避）
      const delay = Math.min(1000 * Math.pow(2, this.wakeWordRetryCount - 1), 10000);
      console.log(`[VoiceAssistant] ${delay}ms 后重连唤醒词监听`);
      
      this.wakeWordReconnectTimer = setTimeout(async () => {
        if (this.isWakeWordListening && !this.voiceModalVisible) {
          try {
            // 重新初始化识别器
            await this.initWakeWordRecognizer();
            
            console.log('[VoiceAssistant] 唤醒词监听重连成功');
            
            // 上报重连成功
            this.reportWakeWordEvent('reconnect_success', { 
              retryCount: this.wakeWordRetryCount 
            });
            
          } catch (error) {
            console.error('[VoiceAssistant] 唤醒词监听重连失败:', error);
            
            // 继续重试
            this.reconnectWakeWordListening();
          }
        }
      }, delay);
    },
    
    // 清理唤醒词连接
    cleanupWakeWordConnection() {
      // 停止心跳
      this.stopWakeWordHeartbeat();
      
      // 停止识别器
      if (this.wakeWordRecognizer) {
        try {
          this.wakeWordRecognizer.stop();
        } catch (e) {
          console.error('[VoiceAssistant] 停止唤醒词识别器失败:', e);
        }
        this.wakeWordRecognizer = null;
      }
      
      this.isWakeWordCanSendData = false;
    },
    
    // 停止唤醒词监听
    stopWakeWordListening(playSound = false) {
      console.log('[VoiceAssistant] 停止唤醒词监听, playSound:', playSound);
      
      this.isWakeWordListening = false;
      
      // 清除重连定时器
      if (this.wakeWordReconnectTimer) {
        clearTimeout(this.wakeWordReconnectTimer);
        this.wakeWordReconnectTimer = null;
      }
      
      // 清理连接
      this.cleanupWakeWordConnection();
      
      // 停止录音器
      if (this.wakeWordRecorder) {
        // 只有在需要时才播放提示音（例如用户手动关闭唤醒词）
        if (playSound) {
          this.playListeningSound(800, 0.08, 0.3);
          setTimeout(() => {
            this.playListeningSound(1000, 0.08, 0.3);
          }, 100);
        }
        try {
          this.wakeWordRecorder.stop();
        } catch (e) {
          console.error('[VoiceAssistant] 停止唤醒词录音器失败:', e);
        }
        this.wakeWordRecorder = null;
      }
      
      // 清除静默计时器
      if (this.silenceTimer) {
        clearTimeout(this.silenceTimer);
        this.silenceTimer = null;
      }
      
      // 重置重试计数
      this.wakeWordRetryCount = 0;
      
      // 注意：不在这里重置 isWakeWordTriggered，因为停止监听不代表唤醒流程结束
      // isWakeWordTriggered 会在用户录音结束后重置
    },
    
    // 暂停唤醒词监听（用户主动录音时）
    pauseWakeWordListening() {
      console.log('[VoiceAssistant] 暂停唤醒词监听');
      
      // 标记为暂停状态（而不是完全停止）
      this.isWakeWordListening = false;
      
      // 停止录音器但保留配置
      if (this.wakeWordRecorder) {
        try {
          this.wakeWordRecorder.stop();
        } catch (e) {
          console.error('[VoiceAssistant] 暂停唤醒词录音器失败:', e);
        }
      }
      
      // 清理连接但不重置重试计数
      this.cleanupWakeWordConnection();
    },
    
    // 恢复唤醒词监听（用户录音结束后）
    async resumeWakeWordListening() {
      if (!this.wakeWordEnabled) {
        console.log('[VoiceAssistant] 唤醒词未启用，不恢复监听');
        return;
      }
      
      if (this.isWakeWordListening) {
        console.log('[VoiceAssistant] 唤醒词监听已在运行，无需恢复');
        return;
      }
      
      console.log('[VoiceAssistant] 恢复唤醒词监听');
      
      // 重新启动唤醒词监听
      await this.startWakeWordListening();
    },
    
    // 重启唤醒词监听
    async restartWakeWordListening() {
      if (!this.wakeWordEnabled) return;
      
      console.log('[VoiceAssistant] 重启唤醒词监听');
      
      this.stopWakeWordListening();
      
      // 等待一小段时间再重启
      await new Promise(resolve => setTimeout(resolve, 500));
      
      if (this.wakeWordEnabled && !this.voiceModalVisible) {
        await this.startWakeWordListening();
      }
    },
    
    // 唤醒词检测到后的处理
    async onWakeWordDetected(wakeText, userQuery = '') {
      console.log('[VoiceAssistant] 唤醒词已触发:', wakeText);
      console.log('[VoiceAssistant] 用户需求:', userQuery);
      
      // 播放唤醒成功提示音 (双音调表示唤醒)
      this.playListeningSound(800, 0.08, 0.3);
      setTimeout(() => {
        this.playListeningSound(1000, 0.08, 0.3);
      }, 100);
      
      // 注意：不要停止唤醒词监听！让它继续运行以接收用户后续输入
      // 唤醒词检测器会通过 OnRecognitionResultChange 和 OnSentenceEnd 持续接收识别结果
      // checkWakeWord 方法会继续累积用户需求，直到3秒静默后自动执行
      
      // 震动反馈
      if (navigator.vibrate) {
        navigator.vibrate([50, 100, 50]);
      }
      
      // 打开语音助手弹窗
      if (!this.voiceModalVisible) {
        this.showVoiceModal();
      }
      
      // 如果用户在唤醒的同时说了需求，直接处理
      if (userQuery && userQuery.length > 0) {
        console.log('[VoiceAssistant] 检测到唤醒时的用户需求，直接处理:', userQuery);
        
        // 停止唤醒词监听，因为已经有完整需求了
        this.stopWakeWordListening();
        
        // 显示提示
        this.$toast({
          message: '已识别您的需求',
          type: 'success',
          duration: 1500
        });
        
        // 设置识别文本（去除唤醒词，只保留用户需求）
        this.recognitionText = this.replaceRecognitionText(userQuery);
        
        // 直接调用AI接口处理用户需求
        if (this.autoCallAI) {
          await this.callHunyuanAPI(userQuery);
        }
        
        return;
      }
      
      // 没有检测到用户需求，继续监听
      // 显示提示
      // this.$toast({
      //   message: '已唤醒，请说话...',
      //   type: 'success',
      //   duration: 1500
      // });
      
      console.log('[VoiceAssistant] 唤醒词检测器继续监听用户输入，等待用户说出需求');
      
      // 重新设置 pendingWakeText，让 checkWakeWord 能够继续累积用户输入
      // 使用一个标记文本表示已经唤醒，等待用户需求
      this.pendingWakeText = wakeText || '已唤醒';
      this.pendingUserQuery = '';
      
      // 启动3秒防抖计时器，等待用户继续说话
      if (this.wakeWordDebounceTimer) {
        clearTimeout(this.wakeWordDebounceTimer);
      }
      this.wakeWordDebounceTimer = setTimeout(() => {
        console.log('[VoiceAssistant] 用户3秒未说话，停止监听');
        
        // 停止唤醒词监听
        this.stopWakeWordListening();
        
        // 如果有识别内容，进入AI分析
        if (this.recognitionText && this.recognitionText.trim()) {
          console.log('[VoiceAssistant] 有识别内容，进入AI分析:', this.recognitionText);
          if (this.autoCallAI) {
            this.callHunyuanAPI(this.recognitionText);
          }
        } else {
          // 没有识别内容，提示用户
          console.log('[VoiceAssistant] 无识别内容，提示用户');
          this.speakText('您还在吗');
          
          // 关闭弹窗
          setTimeout(() => {
            if (this.voiceModalVisible) {
              this.closeVoiceModal();
            }
            
            // 重新启动唤醒词监听
            if (this.wakeWordEnabled) {
              setTimeout(() => {
                this.startWakeWordListening();
              }, 1000);
            }
          }, 1500);
        }
        
        // 清空待处理数据
        this.pendingWakeText = '';
        this.pendingUserQuery = '';
        this.wakeWordDebounceTimer = null;
        
        // 重置触发标志
        this.isWakeWordTriggered = false;
      }, this.wakeWordDebounceDelay);
    },
    
    // 启动静默检测
    startSilenceDetection() {
      // 清除之前的计时器
      if (this.silenceTimer) {
        clearTimeout(this.silenceTimer);
      }
      
      // 设置新的计时器
      this.silenceTimer = setTimeout(() => {
        console.log('[VoiceAssistant] 检测到静默超时，自动进入AI分析');
        
        // 如果有识别内容，自动停止录音并进入AI分析
        if (this.recognitionText && this.recognitionText.trim()) {
          console.log('[VoiceAssistant] 有识别内容，自动停止录音:', this.recognitionText);
          
          // 播放提示音表示已接收
          this.playListeningSound(800, 0.08, 0.2);
          
          // 停止录音（会自动触发AI分析）
          if (this.isRecording) {
            this.stopRecording();
          }
        } else {
          // 没有识别内容，播放提示音询问
          console.log('[VoiceAssistant] 无识别内容，播放提示音');
          this.speakText('您还在吗');
          
          // 等待语音播报完成后再关闭
          setTimeout(() => {
            // 停止录音
            if (this.isRecording) {
              this.stopRecording();
            }
            
            // 关闭弹窗
            if (this.voiceModalVisible) {
              this.closeVoiceModal();
            }
            
            // 重新启动唤醒词监听
            if (this.wakeWordEnabled) {
              setTimeout(() => {
                this.startWakeWordListening();
              }, 1000);
            }
          }, 1500);
        }
        
        // 上报静默超时事件
        this.reportWakeWordEvent('silence_timeout');
        
      }, this.silenceTimeout);
    },
    
    // 重置静默计时器
    resetSilenceTimer() {
      if (this.silenceTimer) {
        clearTimeout(this.silenceTimer);
        this.startSilenceDetection();
      }
    },
    
    // 处理唤醒词错误
    handleWakeWordError(error) {
      console.error('[VoiceAssistant] 唤醒词监听错误:', error);
      
      // 上报错误
      this.reportWakeWordEvent('error', { 
        error: error.message || error.toString(),
        retryCount: this.wakeWordRetryCount
      });
      
      // 触发重连
      if (this.wakeWordEnabled && this.isWakeWordListening) {
        this.reconnectWakeWordListening();
      }
    },
    
    // 上报唤醒词事件
    reportWakeWordEvent(eventType, extraData = {}) {
      try {
        const { aegis } = this.$store.state.user;
        if (aegis) {
          aegis.infoAll({
            msg: JSON.stringify({
              type: 'wake_word_event',
              eventType,
              timestamp: new Date().toISOString(),
              ...extraData
            }),
            ext1: 'voice-assistant-wake-word',
            trace: `wake-word-${eventType}`,
          });
        }
      } catch (error) {
        console.error('[VoiceAssistant] 上报唤醒词事件失败:', error);
      }
    },
    
    // 语音播报文字（对话模式下使用）
    speakText(text) {
      try {
        // 检查浏览器是否支持语音合成
        if (!window || !window.speechSynthesis || typeof window.speechSynthesis.speak !== 'function') {
          console.warn('[VoiceAssistant] 浏览器不支持语音合成');
          return;
        }

        // 检查文本是否有效
        if (!text || typeof text !== 'string') {
          console.warn('[VoiceAssistant] 无效的语音文本');
          return;
        }

        // 停止当前正在播放的语音
        if (this.isSpeaking && typeof window.speechSynthesis.cancel === 'function') {
          window.speechSynthesis.cancel();
          this.isSpeaking = false;
          this.currentUtterance = null;
        }

        // 创建语音合成实例
        const utterance = new SpeechSynthesisUtterance(text);
        this.currentUtterance = utterance;
        
        // 设置语音参数
        utterance.lang = 'zh-CN'; // 中文
        utterance.rate = 1.1; // 语速稍快
        utterance.pitch = 1.0; // 音调
        utterance.volume = 1.0; // 音量

        // 添加事件监听
        utterance.onstart = () => {
          this.isSpeaking = true;
          console.log('[VoiceAssistant] 开始播放语音:', text);
        };

        utterance.onend = () => {
          this.isSpeaking = false;
          this.currentUtterance = null;
          console.log('[VoiceAssistant] 语音播放完成');
        };

        utterance.onerror = (event) => {
          console.error('[VoiceAssistant] 语音播放错误:', event);
          this.isSpeaking = false;
          this.currentUtterance = null;
        };

        // 开始播放
        window.speechSynthesis.speak(utterance);
        
      } catch (error) {
        console.error('[VoiceAssistant] 语音播报失败:', error);
        this.isSpeaking = false;
        this.currentUtterance = null;
      }
    },
    
    // 停止语音播报
    stopSpeaking() {
      try {
        if (window && window.speechSynthesis && typeof window.speechSynthesis.cancel === 'function') {
          window.speechSynthesis.cancel();
          this.isSpeaking = false;
          this.currentUtterance = null;
          console.log('[VoiceAssistant] 已停止语音播报');
        }
      } catch (error) {
        console.error('[VoiceAssistant] 停止语音播报失败:', error);
      }
    },
    
    // ==================== 其他方法 ====================
    
    // 文本替换函数 - 将语音识别中的错误词汇替换为正确词汇
    replaceRecognitionText(text) {
      if (!text) return text;
      
      // 定义替换规则
      const replacements = [
        { pattern: /顾姿/g, replacement: '固资' },
        { pattern: /顾子/g, replacement: '固资' },
        { pattern: /固子/g, replacement: '固资' },
        { pattern: /故资/g, replacement: '固资' },
        { pattern: /故子/g, replacement: '固资' },
        { pattern: /古资/g, replacement: '固资' },
        { pattern: /古子/g, replacement: '固资' },
        { pattern: /谷子/g, replacement: '固资' },
        { pattern: /谷资/g, replacement: '固资' },
      ];
      
      let result = text;
      replacements.forEach(({ pattern, replacement }) => {
        result = result.replace(pattern, replacement);
      });
      
      return result;
    },
    
    // 启动示例文本滚动
    startExampleScroll() {
      this.exampleScrollTimer = setInterval(() => {
        this.currentExampleIndex = (this.currentExampleIndex + 1) % this.exampleTexts.length;
      }, 3000); // 每3秒切换一次
    },

    // 停止示例文本滚动
    stopExampleScroll() {
      if (this.exampleScrollTimer) {
        clearInterval(this.exampleScrollTimer);
        this.exampleScrollTimer = null;
      }
    },

    // 播放监听提示音
    // 参数说明:
    // - frequency: 频率(Hz), 默认 800Hz (常用范围: 400-1200Hz)
    // - duration: 持续时间(秒), 默认 0.15秒
    // - volume: 音量 (0-1), 默认 0.3
    playListeningSound(frequency = 800, duration = 0.15, volume = 0.3) {
      try {
        // 使用 Web Audio API 生成固定赫兹的提示音
        const audioContext = new (window.AudioContext || window.webkitAudioContext)();
        
        // 创建振荡器（生成音频信号）
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();
        
        // 设置音频参数
        oscillator.type = 'sine'; // 正弦波，声音更柔和
        oscillator.frequency.setValueAtTime(frequency, audioContext.currentTime);
        
        // 设置音量包络（淡入淡出效果，避免爆音）
        const fadeInTime = Math.min(0.01, duration * 0.1); // 淡入时间
        const fadeOutTime = Math.min(0.05, duration * 0.3); // 淡出时间
        const sustainTime = duration - fadeInTime - fadeOutTime; // 保持时间
        
        gainNode.gain.setValueAtTime(0, audioContext.currentTime);
        gainNode.gain.linearRampToValueAtTime(volume, audioContext.currentTime + fadeInTime); // 淡入
        gainNode.gain.linearRampToValueAtTime(volume, audioContext.currentTime + fadeInTime + sustainTime); // 保持
        gainNode.gain.linearRampToValueAtTime(0, audioContext.currentTime + duration); // 淡出
        
        // 连接音频节点
        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);
        
        // 播放音频
        oscillator.start(audioContext.currentTime);
        oscillator.stop(audioContext.currentTime + duration);
        
        console.log(`[VoiceAssistant] 播放监听提示音 (${frequency}Hz, ${duration}s, 音量${volume})`);
      } catch (error) {
        console.error('[VoiceAssistant] 播放监听提示音失败:', error);
      }
    },

    // 语音按钮触摸开始 (移动端)
    async handleVoiceTouchStart(event) {
      if (this.isProcessing || this.isApiProcessing) return;
      
      // 如果没有麦克风权限，先申请权限
      if (!this.microphonePermissionGranted) {
        await this.requestMicrophonePermission();
        if (!this.microphonePermissionGranted) return; // 权限申请失败，退出
      }
      
      const touch = event.touches[0];
      this.touchStartX = touch.clientX;
      this.touchStartY = touch.clientY;
      this.isCancelGesture = false;
      this.pressStartTime = Date.now();
      this.isLongPress = false;
      
      // 设置长按检测定时器
      this.longPressTimer = setTimeout(() => {
        this.isLongPress = true;
        this.startRecording();
      }, this.longPressThreshold);
    },

    // 语音按钮触摸移动 (移动端)
    handleVoiceTouchMove(event) {
      if (!this.isRecording || !this.isLongPress) return;
      
      const touch = event.touches[0];
      const deltaY = Math.abs(touch.clientY - this.touchStartY);
      const deltaX = Math.abs(touch.clientX - this.touchStartX);
      
      // 检查是否超过取消阈值
      if (deltaY > this.cancelThreshold || deltaX > this.cancelThreshold) {
        this.isCancelGesture = true;
      } else {
        this.isCancelGesture = false;
      }
    },

    // 语音按钮触摸结束 (移动端)
    handleVoiceTouchEnd(event) {
      // 清除长按检测定时器
      if (this.longPressTimer) {
        clearTimeout(this.longPressTimer);
        this.longPressTimer = null;
      }
      
      const pressDuration = Date.now() - this.pressStartTime;
      
      if (this.isLongPress) {
        // 长按模式：松开结束录音
        if (this.isCancelGesture) {
          this.cancelRecording();
        } else if (this.isRecording) {
          this.stopRecording();
        }
      } else if (pressDuration < this.longPressThreshold) {
        // 短按模式：点击切换录音状态
        if (this.isClickRecording) {
          this.stopRecording();
        } else {
          this.startClickRecording();
        }
      }
      
      this.isCancelGesture = false;
      this.isLongPress = false;
    },

    // 语音按钮鼠标按下 (桌面端)
    async handleVoiceMouseDown(event) {
      if (this.isProcessing || this.isApiProcessing) return;
      
      // 如果没有麦克风权限，先申请权限
      if (!this.microphonePermissionGranted) {
        await this.requestMicrophonePermission();
        if (!this.microphonePermissionGranted) return; // 权限申请失败，退出
      }
      
      this.touchStartX = event.clientX;
      this.touchStartY = event.clientY;
      this.isCancelGesture = false;
      this.pressStartTime = Date.now();
      this.isLongPress = false;
      
      // 设置长按检测定时器
      this.longPressTimer = setTimeout(() => {
        this.isLongPress = true;
        this.startRecording();
      }, this.longPressThreshold);
    },

    // 语音按钮鼠标抬起 (桌面端)
    handleVoiceMouseUp(event) {
      // 清除长按检测定时器
      if (this.longPressTimer) {
        clearTimeout(this.longPressTimer);
        this.longPressTimer = null;
      }
      
      const pressDuration = Date.now() - this.pressStartTime;
      
      if (this.isLongPress) {
        // 长按模式：松开结束录音
        if (this.isRecording) {
          this.stopRecording();
        }
      } else if (pressDuration < this.longPressThreshold) {
        // 短按模式：点击切换录音状态
        if (this.isClickRecording) {
          this.stopRecording();
        } else {
          this.startClickRecording();
        }
      }
      
      this.isCancelGesture = false;
      this.isLongPress = false;
    },

    // 语音按钮鼠标离开 (桌面端)
    handleVoiceMouseLeave(event) {
      // 清除长按检测定时器
      if (this.longPressTimer) {
        clearTimeout(this.longPressTimer);
        this.longPressTimer = null;
      }
      
      if (this.isLongPress && this.isRecording) {
        this.cancelRecording();
      }
      
      this.isCancelGesture = false;
      this.isLongPress = false;
    },

    // 语音按钮点击 (兼容性处理)
    handleVoiceClick(event) {
      // 这个方法主要用于兼容性，实际逻辑在 mouseup/touchend 中处理
      // 防止重复触发
      event.preventDefault();
    },

    // 开始点击录音模式
    startClickRecording() {
      console.log('[VoiceAssistant] 开始点击录音模式');
      this.isClickRecording = true;
      this.startRecording();
    },

    // 取消录音
    cancelRecording() {
      console.log('[VoiceAssistant] 取消录音');
      
      // 上报取消录音事件
      const { aegis } = this.$store.state.user;
      if (aegis) {
        aegis.infoAll({
          msg: JSON.stringify({ 
            action: 'cancel-recording', 
            timestamp: Date.now(),
            conversationMode: this.conversationMode
          }),
          ext1: 'voice-assistant-cancel-recording',
          trace: 'voice-assistant-cancel-recording',
        });
      }
      
      // 清理录音状态
      this.isRecording = false;
      this.isClickRecording = false;
      this.isCancelGesture = false;
      this.statusText = ''; // 清除状态文本
      
      // 清理计时器
      this.clearRecordingTimers();
      
      // 停止录音器
      if (this.recorder) {
        try {
          this.recorder.stop();
        } catch (error) {
          console.error('[VoiceAssistant] 停止录音器失败:', error);
        }
      }
      
      // 重置状态
      this.resetRecording();
      
      this.$toast({
        message: '已取消录音',
        type: 'info',
        duration: 1500
      });
    },

    // 清理录音相关计时器
    clearRecordingTimers() {
      if (this.recordingTimer) {
        clearTimeout(this.recordingTimer);
        this.recordingTimer = null;
      }
      
      if (this.recordingTimeoutTimer) {
        clearTimeout(this.recordingTimeoutTimer);
        this.recordingTimeoutTimer = null;
      }
      
      if (this.longPressTimer) {
        clearTimeout(this.longPressTimer);
        this.longPressTimer = null;
      }
    },

    initUsers() {
      try {
        const users = JSON.parse(this.getConfigItem('showVoice'));
        if (users?.includes?.(sessionStorage.getItem('userid'))) {
          this.showVoice = true
        }
      } catch (e) {
      }

    },
    // 初始化语音识别配置
    initSpeechConfig() {
      // 从七彩石配置获取语音识别相关配置

      try {
        const config = JSON.parse(this.getConfigItem('voiceConfig'));
        this.speechConfig.appid = config?.app;
        this.speechConfig.secretKey = atob((config.id || '').slice(1));
        this.speechConfig.secretid = atob((config.key || '').slice(0, -1));
      } catch (e) {
      }

    },

    // 显示语音助手弹窗
    async showVoiceModal() {
      this.voiceModalVisible = true;
      
      // 打开弹窗时自动申请麦克风权限
      if (!this.microphonePermissionGranted) {
        console.log('[VoiceAssistant] 弹窗打开，自动申请麦克风权限');
        await this.requestMicrophonePermission();
      }
    },

    // 关闭语音助手弹窗
    closeVoiceModal() {
      console.log('[VoiceAssistant] 关闭弹窗，停止所有语音相关功能');
      
      // 1. 停止录音
      if (this.isRecording || this.isClickRecording) {
        console.log('[VoiceAssistant] 停止录音');
        // 直接停止录音器和识别器，不触发后续处理
        if (this.recorder) {
          try {
            this.recorder.stop();
          } catch (e) {
            console.error('[VoiceAssistant] 停止录音器失败:', e);
          }
        }
        
        if (this.speechRecognizer) {
          try {
            if (this.isCanStop) {
              this.speechRecognizer.stop();
            }
          } catch (e) {
            console.error('[VoiceAssistant] 停止语音识别失败:', e);
          }
        }
        
        this.isRecording = false;
        this.isClickRecording = false;
        this.isCanSendData = false;
        this.isCanStop = false;
      }

      // 2. 停止语音播报
      if (window && window.speechSynthesis && typeof window.speechSynthesis.cancel === 'function') {
        window.speechSynthesis.cancel();
        console.log('[VoiceAssistant] 已停止语音播报');
      }
      this.isSpeaking = false;
      this.currentUtterance = null;
      
      // 3. 中断AI处理
      if (this.isApiProcessing) {
        console.log('[VoiceAssistant] 中断AI处理');
        this.isApiProcessing = false;
      }
      
      // 4. 清除所有计时器
      this.clearRecordingTimers();
      
      // 清除静默计时器
      if (this.silenceTimer) {
        clearTimeout(this.silenceTimer);
        this.silenceTimer = null;
      }
      
      // 清除唤醒词防抖计时器
      if (this.wakeWordDebounceTimer) {
        clearTimeout(this.wakeWordDebounceTimer);
        this.wakeWordDebounceTimer = null;
      }

      // 5. 重置所有状态
      this.voiceModalVisible = false;
      this.isProcessing = false;
      this.resetRecording();

      // 重置识别结果展示内容（但不清空对话历史）
      this.recognitionText = '';
      this.resultText = '';
      this.statusText = '';
      this.isCancelGesture = false;
      
      // 重置唤醒词触发标志
      this.isWakeWordTriggered = false;
      this.pendingUserQuery = '';
      this.pendingWakeText = '';
      
      // 6. 如果启用了唤醒词，重新启动监听
      if (this.wakeWordEnabled && !this.isWakeWordListening) {
        console.log('[VoiceAssistant] 弹窗关闭，重新启动唤醒词监听');
        setTimeout(() => {
          this.startWakeWordListening();
        }, 500);
      }
      
      console.log('[VoiceAssistant] 所有语音功能已停止');
      
      // 注意：不清空 conversationMode、conversationHistory 和 fullConversationHistory
      // 这样下次打开时可以继续之前的对话
    },

    // 清空识别结果
    clearResult() {
      this.recognitionText = '';
      this.resultText = '';
      this.statusText = '';
    },

    // 重置识别结果展示内容
    resetDisplayContent() {
      // 清空所有显示内容
      this.recognitionText = '';
      this.resultText = '';
      this.statusText = '';

      // 重置按钮状态
      this.buttonText = '按住说话';
      this.isProcessing = false;
      this.isApiProcessing = false; // 重置API处理状态
      
      // 重置对话模式（仅在非对话模式下）
      if (!this.conversationMode) {
        this.conversationHistory = [];
      }

      console.log('[VoiceAssistant] 已重置显示内容');
    },

    // 调用混元AI接口
    async callHunyuanAPI(query) {
      try {
        console.log('[VoiceAssistant] 调用混元AI接口，Query:', query);
        this.isApiProcessing = true; // 设置接口调用状态
        this.statusText = 'AI正在分析中...';
        
        // 上报 AI 处理开始
        try {
          const { aegis } = this.$store.state.user;
          if (aegis) {
            aegis.infoAll({
              msg: JSON.stringify({
                type: 'ai_processing_start',
                query,
                queryLength: query?.length || 0,
                conversationMode: this.conversationMode,
                historyLength: this.fullConversationHistory.length,
                timestamp: new Date().toISOString()
              }),
              ext1: 'voice-assistant-ai',
              trace: 'ai-processing-start',
            });
          }
        } catch (error) {
          console.error('[VoiceAssistant] 上报AI处理开始失败:', error);
        }
        
        // 准备对话记录参数
        // 对话模式下：只传递最近的3轮对话（6条消息）
        // 非对话模式下：传递空数组或完整历史
        let messagesToSend = [];
        if (this.conversationMode && this.fullConversationHistory.length > 0) {
          // 取最近的6条消息（3轮对话）
          messagesToSend = this.fullConversationHistory.slice(-6);
          console.log('[VoiceAssistant] 对话模式：传递最近3轮对话记录');
        } else {
          messagesToSend = this.fullConversationHistory;
        }
        
        const conversationParams = {
          messages: messagesToSend
        };
        
        console.log('[VoiceAssistant] 传入对话记录:', conversationParams);
        console.log('[VoiceAssistant] 对话记录数量:', messagesToSend.length);
        
        // 调用postHunyuan函数，传入Query、ForwardService和对话记录
        const result = await postHunyuan(query, 'hyaide-application-19337', conversationParams);

        console.log('[VoiceAssistant] 混元AI接口响应结果:', JSON.parse(result?.data?.data?.result));
        const { aegis } = this.$store.state.user;
            aegis.infoAll({
              msg: JSON.stringify({query, result: result?.data?.data?.result}),
              ext1: 'ai-button-click',
              trace: 'ai-button-click',
            });
        
        const parsedResult = JSON.parse(result?.data?.data?.result);
        console.log(parsedResult,"parsedResult");
        
        // 检查是否包含 ask 字段，如果有则进入对话模式
        if (parsedResult?.ask) {
          this.conversationMode = true;
          
          // 添加用户消息和AI回复到完整历史
          this.fullConversationHistory.push({
            role: 'user',
            content: query
          });
          
          this.fullConversationHistory.push({
            role: 'assistant',
            content: parsedResult.ask
          });
          
          // 限制历史记录并保存
          this.limitConversationHistory();
          
          // 展示最近3轮（6条消息）
          this.conversationHistory = this.fullConversationHistory.slice(-6);
          
          // 播放AI回复的语音
          this.speakText(parsedResult.ask);
          
          this.statusText = 'AI需要更多信息';
          this.$nextTick(() => {
            this.scrollToBottom();
          });
        } else if (parsedResult?.tool === 'form') {
            // 过滤 schema properties 的函数（不修改原数据）
            const filterSchemaProperties = (originalData) => {
              if (!originalData?.operate_form_schema?.schema?.properties) {
                return originalData?.operate_form_schema?.schema || {};
              }
            
              const allowedComponents = JSON.parse(this.getConfigItem('AIComponent'));
              console.log("七彩石数据", allowedComponents);
            
              // 深拷贝原数据的 schema 部分
              const originalSchema = originalData.operate_form_schema.schema;
              const filteredSchema = JSON.parse(JSON.stringify(originalSchema));
              const properties = filteredSchema.properties;
            
              // 获取所有需要删除的key
              const keysToDelete = Object.keys(properties).filter(key => {
                const component = properties[key]?.['x-component'];
                return !allowedComponents.includes(component);
              });
            
              // 删除不符合条件的属性
              keysToDelete.forEach(key => {
                delete properties[key];
              });
            
              return filteredSchema;
            };

            const { result } = this.$store.getters['voiceAI/getVoiceAIConfig'];
            console.log(result,"getVoiceAIConfig");

            // 处理flowdata数据
            const getOperateFormData = async () => {
              try {
                const voiceAIStr = JSON.parse(result);

                if (!voiceAIStr) return null;
                const voiceAIData = voiceAIStr;
                return voiceAIData?.operate_form_data || null;
              } catch (error) {
                console.error('获取 operate_form_data 失败:', error);
                return null;
              }
            };
            // 获取并过滤数据
            const voiceAIStr = result;
            const originalData = voiceAIStr ? JSON.parse(voiceAIStr) : {};
            const filteredSchema = filterSchemaProperties(originalData);
            
            console.log('过滤后的 schema:', filteredSchema);

            const createConversationParams = async () => {
              const operateFormData = await getOperateFormData();

              return {
                messages: messagesToSend,
                context: JSON.stringify({
                  schema: JSON.stringify(filteredSchema),
                  flowdata: JSON.stringify(operateFormData),
                }),
              };
            };
            const newConversationParams = await createConversationParams();
            console.log(newConversationParams,"newConversationParams");  

            // 调用postHunyuan函数，传入Query、ForwardService和对话记录
            const newResult = await postHunyuan(query, 'hyaide-application-20226', newConversationParams);
            const newParsedResult = JSON.parse(newResult?.data?.data?.result);
            console.log(newParsedResult,"newResult");
            if (newParsedResult?.warnings.length > 0) {
                this.conversationMode = true;
          
                // 添加用户消息和AI回复到完整历史
                this.fullConversationHistory.push({
                  role: 'user',
                  content: query
                });
                
                this.fullConversationHistory.push({
                  role: 'assistant',
                  content: newParsedResult.warnings[0]
                });
                
                // 限制历史记录并保存
                this.limitConversationHistory();
                
                // 展示最近3轮（6条消息）
                this.conversationHistory = this.fullConversationHistory.slice(-6);
                
                // 播放AI回复的语音
                this.speakText(newParsedResult.warnings[0]);
                
                this.statusText = 'AI需要更多信息';
                this.$nextTick(() => {
                  this.scrollToBottom();
                });
            } else {
                // 获取动态key和value
                const dataObj = newParsedResult.data;
                console.log(dataObj,"dataObj");

                // 获取所有需要更新的字段
                const updateFields = Object.keys(dataObj);
                console.log(updateFields,"updateFields");
                
                if (updateFields.length === 0) throw new Error('未找到有效的数据字段');

                  // 获取localStorage中的数据
                  const taskId = new URLSearchParams(window.location.search).get('taskId');
                  const storageKey = `taskId=${taskId}`;
                  console.log(storageKey, 'storageKey');

                  const storedData = localStorage.getItem(storageKey);

                  const dataArray = JSON.parse(storedData);
                  console.log(dataArray, 'dataArray');

                  // 更新operateFormData
                  if (Array.isArray(dataArray) && dataArray.length > 0) {
                    dataArray.forEach((item) => {
                      if (item.operateFormData) {
                        // 遍历所有需要更新的字段
                        updateFields.forEach((fieldKey) => {
                          item.operateFormData[fieldKey] = dataObj[fieldKey];
                        });
                      }
                    });

                    localStorage.setItem(storageKey, JSON.stringify(dataArray));
                    console.log(`成功更新字段: ${updateFields.join(', ')}`);

                    console.log(localStorage.getItem(storageKey), ' ');

                    // 发送事件通知父组件数据已更新，而不是刷新页面（使用全局事件总线）
                    this.$root.$emit('voice-data-updated', {
                      storageKey: storageKey,
                      data: dataArray,
                      updatedFields: updateFields,
                      updatedData: dataObj,
                    });

                    // 显示成功提示
                    this.$toast({
                      message: '指令执行成功',
                      type: 'success',
                      duration: 1500
                    });
            
                    // 清除对话历史和 sessionStorage
                    this.clearConversationData();
                    
                    // 退出对话模式
                    this.conversationMode = false;
                    this.conversationHistory = [];
                    this.fullConversationHistory = [];
                    
                    console.log('[VoiceAssistant] 指令执行成功，已清除对话历史并退出对话模式');
                    
                    // 1.5秒后自动关闭弹窗
                    setTimeout(() => {
                      this.closeVoiceModal();
                    }, 1500);
                  }
            }
        } else {
          // 如果没有 ask 字段，说明指令执行成功
          if (this.conversationMode) {
            // 显示成功提示
            this.$toast({
              message: '指令执行成功',
              type: 'success',
              duration: 1500
            });
            
            // 清除对话历史和 sessionStorage
            this.clearConversationData();
            
            // 退出对话模式
            this.conversationMode = false;
            this.conversationHistory = [];
            this.fullConversationHistory = [];
            
            console.log('[VoiceAssistant] 指令执行成功，已清除对话历史并退出对话模式');
            
            // 1.5秒后自动关闭弹窗
            setTimeout(() => {
              this.closeVoiceModal();
            }, 1500);
          }
          
          this.statusText = 'AI分析完成';
        }
        
        this.isApiProcessing = false; // 清除接口调用状态
        
        // 上报 AI 处理成功
        try {
          const { aegis } = this.$store.state.user;
          if (aegis) {
            aegis.infoAll({
              msg: JSON.stringify({
                type: 'ai_processing_success',
                query,
                resultType: parsedResult?.tool || (parsedResult?.ask ? 'conversation' : 'unknown'),
                conversationMode: this.conversationMode,
                timestamp: new Date().toISOString()
              }),
              ext1: 'voice-assistant-ai',
              trace: 'ai-processing-success',
            });
          }
        } catch (error) {
          console.error('[VoiceAssistant] 上报AI处理成功失败:', error);
        }

        // 发出事件，让父组件知道AI接口调用完成
        this.$emit('ai-response', {
          query,
          result
        });
        this.$emit('change', {...parsedResult, query});
        
        // 3秒后清除状态提示（仅在非对话模式下）
        if (!this.conversationMode) {
          setTimeout(() => {
            if (this.statusText === 'AI分析完成') {
              this.statusText = '';
            }
          }, 3000);
        }

      } catch (error) {
        console.error('[VoiceAssistant] 调用混元AI接口失败:', error);
        this.statusText = 'AI接口调用失败';
        this.isApiProcessing = false; // 清除接口调用状态

        // 上报错误到aegis
        try {
          const { aegis } = this.$store.state.user;
          if (aegis) {
            aegis.infoAll({
              msg: JSON.stringify({
                type: 'ai_processing_error',
                query,
                error: error.message || error.toString(),
                stack: error.stack,
                conversationMode: this.conversationMode,
                timestamp: new Date().toISOString()
              }),
              ext1: 'voice-assistant-error',
              trace: 'ai-processing-error',
            });
          }
        } catch (reportError) {
          console.error('[VoiceAssistant] 上报AI处理错误失败:', reportError);
        }

        // 发出错误事件
        this.$emit('ai-error', {
          query,
          error
        });

        // 显示错误提示
        this.$toast({
          message: 'AI接口调用失败',
          type: 'fail',
          duration: 3000
        });

        // 3秒后清除状态提示
        setTimeout(() => {
          if (this.statusText === 'AI接口调用失败') {
            this.statusText = '';
          }
        }, 3000);
      }
    },
    
    // 限制对话历史记录
    limitConversationHistory() {
      // 展示历史：最多保留3轮交互（6条消息）
      const maxDisplayMessages = 6;
      if (this.conversationHistory.length > maxDisplayMessages) {
        this.conversationHistory = this.conversationHistory.slice(-maxDisplayMessages);
      }
      
      // 完整历史：最多保留3轮交互（6条消息）
      const maxFullMessages = 6;
      if (this.fullConversationHistory.length > maxFullMessages) {
        this.fullConversationHistory = this.fullConversationHistory.slice(-maxFullMessages);
      }
      
      // 保存到 sessionStorage
      this.saveConversationToSession();
    },
    
    // 保存对话历史到 sessionStorage
    saveConversationToSession() {
      try {
        const currentPath = this.$route.fullPath;
        const storageKey = `voice_${currentPath}`;
        sessionStorage.setItem(storageKey, JSON.stringify(this.fullConversationHistory));
        console.log('[VoiceAssistant] 保存对话历史到 sessionStorage:', storageKey, this.fullConversationHistory);
      } catch (error) {
        console.error('[VoiceAssistant] 保存对话历史失败:', error);
      }
    },
    
    // 从 sessionStorage 加载对话历史
    loadConversationFromSession() {
      try {
        const currentPath = this.$route.fullPath;
        const storageKey = `voice_${currentPath}`;
        const savedHistory = sessionStorage.getItem(storageKey);
        if (savedHistory) {
          this.fullConversationHistory = JSON.parse(savedHistory);
          // 展示最近3轮（6条消息）
          this.conversationHistory = this.fullConversationHistory.slice(-6);
          // 如果有历史记录，自动进入对话模式
          if (this.fullConversationHistory.length > 0) {
            this.conversationMode = true;
          }
          console.log('[VoiceAssistant] 从 sessionStorage 加载对话历史:', storageKey, this.fullConversationHistory);
        }
      } catch (error) {
        console.error('[VoiceAssistant] 加载对话历史失败:', error);
      }
    },
    
    // 滚动到对话底部
    scrollToBottom() {
      if (this.$refs.conversationMessages) {
        this.$refs.conversationMessages.scrollTop = this.$refs.conversationMessages.scrollHeight;
      }
    },
    
    // 清除对话数据（不需要确认，用于指令执行成功后自动清除）
    clearConversationData() {
      try {
        const currentPath = this.$route.fullPath;
        const storageKey = `voice_${currentPath}`;
        sessionStorage.removeItem(storageKey);
        console.log('[VoiceAssistant] 已清除对话历史缓存:', storageKey);
      } catch (error) {
        console.error('[VoiceAssistant] 清除对话历史缓存失败:', error);
      }
    },
    
    // 清除对话历史（需要用户确认）
    clearConversation() {
      this.$dialog.confirm({
        title: '清除对话',
        message: '确定要清除所有对话记录吗？',
      }).then(() => {
        // 清空对话历史
        this.conversationHistory = [];
        this.fullConversationHistory = [];
        this.conversationMode = false;
        
        // 清空 sessionStorage
        this.clearConversationData();
        
        this.$toast({
          message: '对话记录已清除',
          type: 'success',
          duration: 1500
        });
      }).catch(() => {
        // 用户取消
      });
    },

    // 进入对话模式并添加AI消息（供外部调用）
    enterConversationModeWithMessage(message) {
      console.log('[VoiceAssistant] 进入对话模式，AI消息:', message);
      
      // 上报进入对话模式事件
      const { aegis } = this.$store.state.user;
      if (aegis) {
        aegis.infoAll({
          msg: JSON.stringify({ 
            action: 'enter-conversation-mode', 
            timestamp: Date.now(),
            message: message,
            historyLength: this.fullConversationHistory.length
          }),
          ext1: 'voice-assistant-enter-conversation',
          trace: 'voice-assistant-enter-conversation',
        });
      }
      
      // 进入对话模式
      this.conversationMode = true;
      
      // 添加AI消息到对话历史
      this.fullConversationHistory.push({
        role: 'assistant',
        content: message
      });
      
      // 限制历史记录并保存
      this.limitConversationHistory();
      
      // 展示最近3轮（6条消息）
      this.conversationHistory = this.fullConversationHistory.slice(-6);
      
      // 滚动到底部
      this.$nextTick(() => {
        this.scrollToBottom();
      });
      
      // 确保弹窗是打开的
      if (!this.voiceModalVisible) {
        this.voiceModalVisible = true;
      }
    },

    // 切换到对话模式（供外部调用）
    switchToConversationMode() {
      console.log('[VoiceAssistant] 切换到对话模式');
      this.conversationMode = true;
      
      // 确保弹窗是打开的
      if (!this.voiceModalVisible) {
        this.voiceModalVisible = true;
      }
    },

    // 添加助手消息到对话历史（供外部调用）
    addAssistantMessage(message) {
      console.log('[VoiceAssistant] 添加助手消息:', message);
      
      // 添加AI消息到对话历史
      this.fullConversationHistory.push({
        role: 'assistant',
        content: message
      });
      
      // 限制历史记录并保存
      this.limitConversationHistory();
      
      // 展示最近3轮（6条消息）
      this.conversationHistory = this.fullConversationHistory.slice(-6);
      
      // 滚动到底部
      this.$nextTick(() => {
        this.scrollToBottom();
      });
    },

    // 自动开始录音（供外部调用）
    startAutoRecording() {
      console.log('[VoiceAssistant] 自动开始录音');
      
      // 检查是否正在处理中
      if (this.isProcessing || this.isApiProcessing || this.isRecording) {
        console.warn('[VoiceAssistant] 正在处理中，无法开始录音');
        return;
      }
      
      // 模拟点击录音按钮
      this.handleVoiceClick();
    },

    // 悬浮按钮拖拽相关方法
    handleTouchStart(e) {
      const touch = e.touches[0];
      this.startDrag(touch.clientX, touch.clientY);
    },

    handleTouchMove(e) {
      if (this.floatingButton.isDragging) {
        // 阻止页面滚动
        e.preventDefault();
        const touch = e.touches[0];
        this.updateDrag(touch.clientX, touch.clientY);
      }
    },

    handleTouchEnd(e) {
      this.endDrag();
    },

    handleMouseDown(e) {
      this.startDrag(e.clientX, e.clientY);
    },

    handleMouseMove(e) {
      if (this.floatingButton.isDragging) {
        this.updateDrag(e.clientX, e.clientY);
      }
    },

    handleMouseUp(e) {
      this.endDrag();
    },

    startDrag(clientX, clientY) {
      this.floatingButton.isDragging = true;
      this.floatingButton.startX = clientX - this.floatingButton.x;
      this.floatingButton.startY = clientY - this.floatingButton.y;
      this.floatingButton.initialX = this.floatingButton.x;
      this.floatingButton.initialY = this.floatingButton.y;

      // 添加全局事件监听
      document.addEventListener('mousemove', this.handleMouseMove);
      document.addEventListener('mouseup', this.handleMouseUp);

      // 启动长按检测
      this.startLongPressDetection();
    },

    updateDrag(clientX, clientY) {
      if (!this.floatingButton.isDragging) return;

      const newX = clientX - this.floatingButton.startX;
      const newY = clientY - this.floatingButton.startY;

      // 限制按钮在屏幕范围内
      const buttonSize = 36;
      const maxX = window.innerWidth - buttonSize;
      const maxY = window.innerHeight - buttonSize;

      this.floatingButton.x = Math.max(0, Math.min(newX, maxX));
      this.floatingButton.y = Math.max(0, Math.min(newY, maxY));

      // 如果移动了，取消长按检测
      const moved = Math.abs(newX - this.floatingButton.initialX) > 5 || 
                    Math.abs(newY - this.floatingButton.initialY) > 5;
      if (moved) {
        this.cancelLongPressDetection();
      }
    },

    endDrag() {
      if (this.floatingButton.isDragging) {
        this.floatingButton.isDragging = false;

        // 移除全局事件监听
        document.removeEventListener('mousemove', this.handleMouseMove);
        document.removeEventListener('mouseup', this.handleMouseUp);

        // 取消长按检测
        this.cancelLongPressDetection();

        // 吸附到屏幕边缘
        this.snapToEdge();
      }
    },

    snapToEdge() {
      const buttonSize = 36;
      const screenWidth = window.innerWidth;
      const screenHeight = window.innerHeight;
      const threshold = screenWidth / 2;

      // 水平吸附
      if (this.floatingButton.x < threshold) {
        this.floatingButton.x = 5.5; // 吸附到左边
      } else {
        this.floatingButton.x = screenWidth - buttonSize - 5.5; // 吸附到右边
      }

      // 垂直位置限制
      const minY = 70; // 避免遮挡状态栏
      const maxY = screenHeight - buttonSize - 70; // 避免遮挡底部
      this.floatingButton.y = Math.max(minY, Math.min(this.floatingButton.y, maxY));
    },

    handleFloatingButtonClick(e) {
      // 如果是拖拽操作，不触发点击
      const dragDistance = Math.sqrt(
        Math.pow(this.floatingButton.x - this.floatingButton.initialX, 2) +
        Math.pow(this.floatingButton.y - this.floatingButton.initialY, 2)
      );

      if (dragDistance < 100) { // 拖拽距离小于100px才认为是点击
        // 如果弹框已打开，则关闭；否则打开
        if (this.voiceModalVisible) {
          this.closeVoiceModal();
        } else {
          this.showVoiceModal();
        }
      }
    },

    // 长按检测相关方法
    startLongPressDetection() {
      this.isLongPressing = true;
      this.longPressProgress = 125.66; // 重置进度（2 * π * 20）
      
      const startTime = Date.now();
      const updateInterval = 16; // 约60fps
      
      const updateProgress = () => {
        if (!this.isLongPressing) return;
        
        const elapsed = Date.now() - startTime;
        const progress = Math.min(elapsed / this.longPressDuration, 1);
        
        // 更新进度环（从满到空）
        this.longPressProgress = 125.66 * (1 - progress);
        
        if (progress < 1) {
          requestAnimationFrame(updateProgress);
        } else {
          // 长按完成，显示隐藏确认对话框
          this.onLongPressComplete();
        }
      };
      
      requestAnimationFrame(updateProgress);
    },

    cancelLongPressDetection() {
      this.isLongPressing = false;
      this.longPressProgress = 125.66;
    },

    onLongPressComplete() {
      this.isLongPressing = false;
      this.longPressProgress = 125.66;
      
      // 震动反馈（如果支持）
      if (navigator.vibrate) {
        navigator.vibrate(50);
      }
      
      // 检查是否不再显示提示
      const dontShow = localStorage.getItem('voiceAssistant_dontShowHideHint') === 'true';
      
      if (dontShow) {
        // 直接隐藏
        this.hideVoiceButton();
      } else {
        // 显示确认对话框
        this.hideConfirmVisible = true;
      }
    },

    // 确认隐藏语音按钮
    confirmHideVoiceButton() {
      // 保存用户偏好
      if (this.dontShowHideHint) {
        localStorage.setItem('voiceAssistant_dontShowHideHint', 'true');
      }
      
      this.hideVoiceButton();
    },

    // 取消隐藏
    cancelHideVoiceButton() {
      this.hideConfirmVisible = false;
    },

    // 隐藏语音按钮
    hideVoiceButton() {
      this.voiceButtonVisible = false;
      this.hideConfirmVisible = false;
      
      // 保存隐藏状态到 localStorage
      localStorage.setItem('voiceAssistant_buttonVisible', 'false');
      
      // 上报隐藏操作
      try {
        const { aegis } = this.$store.state.user;
        if (aegis) {
          aegis.infoAll({
            msg: JSON.stringify({ action: 'hide', timestamp: Date.now() }),
            ext1: 'voice-assistant-hide',
            trace: 'voice-assistant-hide',
          });
        }
      } catch (error) {
        console.error('[VoiceAssistant] 上报隐藏操作失败:', error);
      }
      
      // 如果弹窗打开，关闭它
      if (this.voiceModalVisible) {
        this.closeVoiceModal();
      }
      
      // 显示提示
      this.$toast({
        message: '语音助手已隐藏，点击右下角小箭头可重新显示',
        duration: 3000
      });
    },

    // 显示语音按钮
    showVoiceButton() {
      this.voiceButtonVisible = true;
      
      // 保存显示状态到 localStorage
      localStorage.setItem('voiceAssistant_buttonVisible', 'true');
      
      // 上报显示操作
      try {
        const { aegis } = this.$store.state.user;
        if (aegis) {
          aegis.infoAll({
            msg: JSON.stringify({ action: 'show', timestamp: Date.now() }),
            ext1: 'voice-assistant-show',
            trace: 'voice-assistant-show',
          });
        }
      } catch (error) {
        console.error('[VoiceAssistant] 上报显示操作失败:', error);
      }
      
      // 显示提示
      this.$toast({
        message: '语音助手已显示',
        duration: 1500
      });
    },

    // 恢复按钮显示状态
    restoreButtonVisibility() {
      const savedVisibility = localStorage.getItem('voiceAssistant_buttonVisible');
      
      // 如果有保存的状态，使用保存的状态
      if (savedVisibility !== null) {
        this.voiceButtonVisible = savedVisibility === 'true';
        console.log('[VoiceAssistant] 恢复按钮显示状态:', this.voiceButtonVisible);
      }
      // 否则默认显示（已在 data 中设置为 true）
    },

    // 初始化悬浮按钮位置
    initFloatingButtonPosition() {
      const buttonSize = 32;
      const screenWidth = window.innerWidth;
      const screenHeight = window.innerHeight;

      // 如果有传入初始位置，使用传入的位置
      if (this.initialPosition.x !== null && this.initialPosition.y !== null) {
        this.floatingButton.x = this.initialPosition.x;
        this.floatingButton.y = this.initialPosition.y;
      } else {
        // 默认固定在屏幕右下角（距离底部80px，距离右边7.5px）
        this.floatingButton.x = screenWidth - buttonSize - 7.5;
        this.floatingButton.y = screenHeight - buttonSize - 150;
      }
    },

    // 处理窗口大小变化
    handleWindowResize() {
      // 重新调整按钮位置，确保不超出屏幕
      const buttonSize = 36;
      const maxX = window.innerWidth - buttonSize;
      const maxY = window.innerHeight - buttonSize;

      this.floatingButton.x = Math.min(this.floatingButton.x, maxX);
      this.floatingButton.y = Math.min(this.floatingButton.y, maxY);

      // 重新吸附到边缘
      this.snapToEdge();
    },

    // 处理页面滚动
    handlePageScroll() {
      // 如果按钮正在被拖拽，不处理滚动隐藏
      if (this.floatingButton.isDragging) {
        return;
      }

      // 标记正在滚动
      this.isScrolling = true;

      // 如果按钮还没有因滚动而隐藏，则隐藏一半
      if (!this.isButtonHiddenByScroll) {
        this.hideButtonHalfOnScroll();
      }

      // 清除之前的计时器
      if (this.scrollTimer) {
        clearTimeout(this.scrollTimer);
      }

      // 设置新的计时器，滚动停止后恢复按钮
      this.scrollTimer = setTimeout(() => {
        this.isScrolling = false;
        this.showButtonAfterScroll();
      }, 150); // 150ms 无滚动后认为滚动停止
    },

    // 滚动时隐藏按钮一半
    hideButtonHalfOnScroll() {
      const buttonSize = 36;
      const screenWidth = window.innerWidth;
      
      // 判断按钮在左侧还是右侧
      const isOnRight = this.floatingButton.x > screenWidth / 2;
      
      if (isOnRight) {
        // 在右侧，隐藏到右边缘（只露出一半）
        this.floatingButton.x = screenWidth - buttonSize / 2;
      } else {
        // 在左侧，隐藏到左边缘（只露出一半）
        this.floatingButton.x = -buttonSize / 2;
      }
      
      this.isButtonHiddenByScroll = true;
    },

    // 滚动停止后恢复按钮
    showButtonAfterScroll() {
      if (!this.isButtonHiddenByScroll) {
        return;
      }

      const buttonSize = 36;
      const screenWidth = window.innerWidth;
      
      // 判断按钮在左侧还是右侧
      const isOnRight = this.floatingButton.x > screenWidth / 2;
      
      if (isOnRight) {
        // 恢复到右侧正常位置
        this.floatingButton.x = screenWidth - buttonSize - 5.5;
      } else {
        // 恢复到左侧正常位置
        this.floatingButton.x = 5.5;
      }
      
      this.isButtonHiddenByScroll = false;
    },

    // 附加滚动监听器到所有可滚动容器
    attachScrollListeners() {
      // 查找所有可能的滚动容器
      const scrollableSelectors = [
        '.van-pull-refresh__track', // van-pull-refresh 的滚动容器
        '.van-list', // van-list 组件
        '.list-box', // 自定义列表容器
        '.todo-list', // 待办列表容器
        '[class*="scroll"]', // 包含 scroll 的类名
        '.van-overflow-hidden' // vant 的滚动容器
      ];

      scrollableSelectors.forEach(selector => {
        const elements = document.querySelectorAll(selector);
        elements.forEach(element => {
          // 检查元素是否可滚动
          const isScrollable = element.scrollHeight > element.clientHeight || 
                               element.scrollWidth > element.clientWidth;
          
          if (isScrollable && !this.scrollContainers.includes(element)) {
            element.addEventListener('scroll', this.handlePageScroll, { passive: true });
            this.scrollContainers.push(element);
            console.log('[VoiceAssistant] 已附加滚动监听:', selector);
          }
        });
      });

      // 使用 MutationObserver 监听 DOM 变化，动态添加滚动监听
      this.scrollObserver = new MutationObserver(() => {
        // 延迟执行，避免频繁触发
        clearTimeout(this.observerTimer);
        this.observerTimer = setTimeout(() => {
          this.attachScrollListeners();
        }, 500);
      });

      // 监听 body 的子节点变化
      this.scrollObserver.observe(document.body, {
        childList: true,
        subtree: true
      });
    },

    // 分离所有滚动监听器
    detachScrollListeners() {
      this.scrollContainers.forEach(element => {
        element.removeEventListener('scroll', this.handlePageScroll);
      });
      this.scrollContainers = [];

      // 停止 MutationObserver
      if (this.scrollObserver) {
        this.scrollObserver.disconnect();
        this.scrollObserver = null;
      }

      if (this.observerTimer) {
        clearTimeout(this.observerTimer);
        this.observerTimer = null;
      }

      console.log('[VoiceAssistant] 已清理所有滚动监听');
    },

    // 申请麦克风权限
    async requestMicrophonePermission() {
      try {
        console.log('[VoiceAssistant] Requesting microphone permission...');

        // 检查浏览器是否支持 getUserMedia
        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
          throw new Error('浏览器不支持录音功能');
        }

        // 申请麦克风权限
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true
          }
        });

        console.log('[VoiceAssistant] Microphone permission granted');

        // 立即停止流，我们只是为了获取权限
        stream.getTracks().forEach(track => track.stop());

        // 更新权限状态
        this.microphonePermissionGranted = true;
        this.statusText = '权限获取成功';

        // 2秒后清除提示
        setTimeout(() => {
          if (this.statusText === '权限获取成功') {
            this.statusText = '';
          }
        }, 2000);

      } catch (error) {
        console.error('[VoiceAssistant] Failed to get microphone permission:', error);

        let errorMessage = '无法获取录音权限';
        if (error.name === 'NotAllowedError') {
          errorMessage = '录音权限被拒绝，请在浏览器设置中允许录音权限';
        } else if (error.name === 'NotFoundError') {
          errorMessage = '未找到麦克风设备';
        } else if (error.name === 'NotSupportedError') {
          errorMessage = '浏览器不支持录音功能';
        }

        this.statusText = errorMessage;
        this.microphonePermissionGranted = false;

        // 显示错误提示
        this.$toast({
          message: errorMessage,
          type: 'fail',
          duration: 5000
        });
      }
    },

    // 鉴权函数相关方法
    toUint8Array(wordArray) {
      const words = wordArray.words;
      const sigBytes = wordArray.sigBytes;
      const u8 = new Uint8Array(sigBytes);
      for (let i = 0; i < sigBytes; i++) {
        u8[i] = (words[i >>> 2] >>> (24 - (i % 4) * 8)) & 0xff;
      }
      return u8;
    },

    Uint8ArrayToString(fileData) {
      let dataString = '';
      for (let i = 0; i < fileData.length; i++) {
        dataString += String.fromCharCode(fileData[i]);
      }
      return dataString;
    },

    // 签名函数示例
    signCallback(signStr) {
      const secretKey = this.speechConfig.secretKey;
      const hash = window.CryptoJSTest.HmacSHA1(signStr, secretKey);
      const bytes = this.Uint8ArrayToString(this.toUint8Array(hash));
      return window.btoa(bytes);
    },
    // 语音助手方法 - 基于腾讯云官方示例改写
    async startRecordingForWake() {
      console.log('[VoiceAssistant] startRecording called');
      try {
        // 防止重复点击或API处理中
        if (this.isRecording || this.isProcessing || this.isApiProcessing) {
          console.log('[VoiceAssistant] Already recording, processing, or API processing, returning');
          return;
        }

        // 如果唤醒词监听开启，暂停唤醒词监听
        if (this.wakeWordEnabled && this.isWakeWordListening) {
          console.log('[VoiceAssistant] 用户主动录音，暂停唤醒词监听');
          this.pauseWakeWordListening();
        }

        // 如果没有麦克风权限，先申请权限
        if (!this.microphonePermissionGranted) {
          await this.requestMicrophonePermission();
          if (!this.microphonePermissionGranted) {
            console.log('[VoiceAssistant] Microphone permission not granted, cannot start recording');
            return;
          }
        }

        // 清空之前的结果
        // this.recognitionText = '';
        // this.resultText = '';
        this.statusText = '正在连接...';

        // 更新按钮状态
        this.isRecording = true;
        if (this.voiceMode === 'click') {
          this.isClickRecording = true;
        }

        // 记录录音开始时间
        this.recordingStartTime = Date.now();
        
        // 设置60秒超时计时器
        this.recordingTimeoutTimer = setTimeout(() => {
          console.log('[VoiceAssistant] 录音超时，自动停止');
          this.$toast({
            message: '录音时间超过60秒，已自动结束',
            type: 'warning',
            duration: 3000
          });
          this.stopRecording();
        }, this.maxRecordingTime);

        // 重置语音识别相关状态
        this.speechRecognizer = null;
        this.isCanSendData = false;
        this.isCanStop = false;

        // 初始化录音器
        this.recorder = new WebRecorder();
        console.log('[VoiceAssistant] Recorder initialized', this.recorder )
        // 设置录音数据回调
        this.recorder.OnReceivedData = (res) => {
          if (this.isCanSendData && this.speechRecognizer) {
            // 发送数据到语音识别服务
            this.speechRecognizer.write(res);
          }
        };

        // 录音失败回调
        this.recorder.OnError = (err) => {
          console.error('[VoiceAssistant] 录音失败:', err);
          
          // 上报错误到aegis
          try {
            const { aegis } = this.$store.state.user;
            if (aegis) {
              aegis.infoAll({
                msg: JSON.stringify({
                  type: 'recorder_error',
                  error: err.message || err.toString(),
                  timestamp: new Date().toISOString()
                }),
                ext1: 'voice-assistant-error',
                trace: 'recorder-error',
              });
            }
          } catch (reportError) {
            console.error('[VoiceAssistant] 上报录音错误失败:', reportError);
          }
          
          this.handleError(err);
        };

        // 开始录音
        this.recorder.start();

        // 初始化语音识别器
        if (!this.speechRecognizer) {
          this.speechRecognizer = new SpeechRecognizer(this.speechConfig);
        }

        // 设置语音识别事件回调
        this.setupSpeechRecognitionCallbacks();

        // 建立语音识别连接
        this.speechRecognizer.start();

      } catch (error) {
        console.error('[VoiceAssistant] Recording failed', error);
        
        // 上报错误到aegis
        try {
          const { aegis } = this.$store.state.user;
          if (aegis) {
            aegis.infoAll({
              msg: JSON.stringify({
                type: 'start_recording_error',
                error: error.message || error.toString(),
                stack: error.stack,
                timestamp: new Date().toISOString()
              }),
              ext1: 'voice-assistant-error',
              trace: 'start-recording-error',
            });
          }
        } catch (reportError) {
          console.error('[VoiceAssistant] 上报录音启动错误失败:', reportError);
        }
        
        this.handleError(error);
      }
    },

    // 语音助手方法 - 基于腾讯云官方示例改写
    async startRecording() {
      console.log('[VoiceAssistant] startRecording called');
      try {
        // 防止重复点击或API处理中
        if (this.isRecording || this.isProcessing || this.isApiProcessing) {
          console.log('[VoiceAssistant] Already recording, processing, or API processing, returning');
          return;
        }

        // 如果唤醒词监听开启，暂停唤醒词监听
        if (this.wakeWordEnabled && this.isWakeWordListening) {
          console.log('[VoiceAssistant] 用户主动录音，暂停唤醒词监听');
          this.pauseWakeWordListening();
        }

        // 如果没有麦克风权限，先申请权限
        if (!this.microphonePermissionGranted) {
          await this.requestMicrophonePermission();
          if (!this.microphonePermissionGranted) {
            console.log('[VoiceAssistant] Microphone permission not granted, cannot start recording');
            return;
          }
        }

        // 清空之前的结果
        this.recognitionText = '';
        this.resultText = '';
        this.statusText = '正在连接...';
        
        // 设置初始化状态
        this.isInitializing = true;

        // 更新按钮状态
        this.isRecording = true;
        if (this.voiceMode === 'click') {
          this.isClickRecording = true;
        }

        // 记录录音开始时间
        this.recordingStartTime = Date.now();
        
        // 设置60秒超时计时器
        this.recordingTimeoutTimer = setTimeout(() => {
          console.log('[VoiceAssistant] 录音超时，自动停止');
          this.$toast({
            message: '录音时间超过60秒，已自动结束',
            type: 'warning',
            duration: 3000
          });
          this.stopRecording();
        }, this.maxRecordingTime);

        // 重置语音识别相关状态
        this.speechRecognizer = null;
        this.isCanSendData = false;
        this.isCanStop = false;

        // 初始化录音器
        this.recorder = new WebRecorder();
        console.log('[VoiceAssistant] Recorder initialized', this.recorder )
        // 设置录音数据回调
        this.recorder.OnReceivedData = (res) => {
          if (this.isCanSendData && this.speechRecognizer) {
            // 发送数据到语音识别服务
            this.speechRecognizer.write(res);
          }
        };

        // 录音失败回调
        this.recorder.OnError = (err) => {
          console.error('[VoiceAssistant] 录音失败:', err);
          
          // 上报错误到aegis
          try {
            const { aegis } = this.$store.state.user;
            if (aegis) {
              aegis.infoAll({
                msg: JSON.stringify({
                  type: 'recorder_error',
                  error: err.message || err.toString(),
                  timestamp: new Date().toISOString()
                }),
                ext1: 'voice-assistant-error',
                trace: 'recorder-error',
              });
            }
          } catch (reportError) {
            console.error('[VoiceAssistant] 上报录音错误失败:', reportError);
          }
          
          this.handleError(err);
        };

        // 开始录音
        this.recorder.start();

        // 初始化语音识别器
        if (!this.speechRecognizer) {
          this.speechRecognizer = new SpeechRecognizer(this.speechConfig);
        }

        // 设置语音识别事件回调
        this.setupSpeechRecognitionCallbacks();

        // 建立语音识别连接
        this.speechRecognizer.start();

      } catch (error) {
        console.error('[VoiceAssistant] Recording failed', error);
        
        // 上报错误到aegis
        try {
          const { aegis } = this.$store.state.user;
          if (aegis) {
            aegis.infoAll({
              msg: JSON.stringify({
                type: 'start_recording_error',
                error: error.message || error.toString(),
                stack: error.stack,
                timestamp: new Date().toISOString()
              }),
              ext1: 'voice-assistant-error',
              trace: 'start-recording-error',
            });
          }
        } catch (reportError) {
          console.error('[VoiceAssistant] 上报录音启动错误失败:', reportError);
        }
        
        this.handleError(error);
      }
    },

    // 设置语音识别事件回调
    setupSpeechRecognitionCallbacks() {
      console.log('[VoiceAssistant] setupSpeechRecognitionCallbacks called', this.speechRecognizer);
      if (!this.speechRecognizer) return;

      // 开始识别
      this.speechRecognizer.OnRecognitionStart = (res) => {
        console.log('开始识别', res);
        this.isCanSendData = true;
        this.isCanStop = true;
        this.statusText = '我正在听...';
        
        // 清除初始化状态
        this.isInitializing = false;
        
        // 上报语音识别开始
        try {
          const { aegis } = this.$store.state.user;
          if (aegis) {
            aegis.infoAll({
              msg: JSON.stringify({
                type: 'speech_recognition_start',
                conversationMode: this.conversationMode,
                timestamp: new Date().toISOString()
              }),
              ext1: 'voice-assistant-recognition',
              trace: 'speech-recognition-start',
            });
          }
        } catch (error) {
          console.error('[VoiceAssistant] 上报语音识别开始失败:', error);
        }
        
        // 启动静默检测（5秒无语音输入自动停止）
        this.startSilenceDetection();
      };

      // 一句话开始
      this.speechRecognizer.OnSentenceBegin = (res) => {
        console.log('一句话开始', res);
        if (res?.voice_text_str) {
          const currentText = `${this.resultText}${res?.voice_text_str}`;
          this.recognitionText = this.replaceRecognitionText(currentText);
        }
      };

      // 识别变化时 - 实时显示识别结果
      this.speechRecognizer.OnRecognitionResultChange = (res) => {
        console.log('识别变化时', res);
        if (res?.voice_text_str) {
          const currentText = `${this.resultText}${res?.voice_text_str}`;
          this.recognitionText = this.replaceRecognitionText(currentText);
          
          // 重置静默计时器（用户正在说话）
          this.resetSilenceTimer();
        }
      };

      // 一句话结束
      this.speechRecognizer.OnSentenceEnd = (res) => {
        console.log('一句话结束', res);
        if (res?.voice_text_str) {
          this.resultText += res?.voice_text_str;
          this.recognitionText = this.replaceRecognitionText(this.resultText);
        }
      };

      // 识别结束
      this.speechRecognizer.OnRecognitionComplete = (res) => {
        console.log('识别结束', res);
        this.isCanSendData = false;
        this.isCanStop = false;
        this.statusText = '识别完成';
        if (!this.recognitionText) {
          this.statusText = '未识别到有效语音';
        }
        
        // 上报语音识别完成
        try {
          const { aegis } = this.$store.state.user;
          if (aegis) {
            aegis.infoAll({
              msg: JSON.stringify({
                type: 'speech_recognition_complete',
                hasText: !!this.recognitionText,
                textLength: this.recognitionText?.length || 0,
                conversationMode: this.conversationMode,
                timestamp: new Date().toISOString()
              }),
              ext1: 'voice-assistant-recognition',
              trace: 'speech-recognition-complete',
            });
          }
        } catch (error) {
          console.error('[VoiceAssistant] 上报语音识别完成失败:', error);
        }
        
        // 发出语音识别完成事件
        this.$emit('voice-recognition-complete', this.recognitionText);

        // 如果开启自动调用AI且有识别结果，自动调用混元AI接口
        if (this.autoCallAI && this.recognitionText && this.recognitionText.trim()) {
          this.callHunyuanAPI(this.recognitionText.trim());
        } else {
          // 如果不自动调用AI，则直接重置录音状态
          this.resetRecording();
        }
      };

      // 识别错误
      this.speechRecognizer.OnError = (res) => {
        console.error('[VoiceAssistant] 语音识别失败:', res);
        
        // 上报错误到aegis
        try {
          const { aegis } = this.$store.state.user;
          if (aegis) {
            aegis.infoAll({
              msg: JSON.stringify({
                type: 'speech_recognition_error',
                error: res,
                timestamp: new Date().toISOString()
              }),
              ext1: 'voice-assistant-error',
              trace: 'speech-recognition-error',
            });
          }
        } catch (reportError) {
          console.error('[VoiceAssistant] 上报语音识别错误失败:', reportError);
        }
        
        // 可以选择是否重置录音状态
        // this.isCanSendData = false;
        // this.isCanStop = false;
        // this.statusText = '识别失败，请重试';
        // this.resetRecording();
      };
    },

    stopRecording() {
      console.log('[VoiceAssistant] stopRecording called, isRecording:', this.isRecording);
      try {
        if (!this.isRecording && !this.isClickRecording) {
          console.log('[VoiceAssistant] Not recording, returning');
          return;
        }

        // 清理计时器
        this.clearRecordingTimers();

        // 更新状态
        this.isRecording = false;
        this.isClickRecording = false;
        this.isCancelGesture = false;
        this.statusText = '正在处理...';

        // 停止录音
        if (this.recorder) {
          this.recorder.stop();
        }

        // 停止语音识别
        if (this.isCanStop && this.speechRecognizer) {
          this.speechRecognizer.stop();
        }

        // 计算录音时长
        const recordingDuration = Date.now() - this.recordingStartTime;
        console.log('[VoiceAssistant] 录音时长:', recordingDuration + 'ms');

        // 重置唤醒词触发标志（录音结束，允许再次唤醒）
        this.isWakeWordTriggered = false;
        console.log('[VoiceAssistant] 录音结束，重置唤醒词触发标志');

        // 如果唤醒词监听开启，恢复唤醒词监听
        if (this.wakeWordEnabled && !this.isWakeWordListening) {
          console.log('[VoiceAssistant] 用户录音结束，恢复唤醒词监听');
          // 延迟1秒后恢复，避免与当前录音冲突
          setTimeout(() => {
            this.resumeWakeWordListening();
          }, 1000);
        }

      } catch (error) {
        console.error('[VoiceAssistant] Stop recording failed', error);
        this.handleError(error);
      }
    },

    // 语音播报文字（对话模式下使用）
    speakText(text) {
      try {
        // 检查浏览器是否支持语音合成
        if (!window || !window.speechSynthesis || typeof window.speechSynthesis.speak !== 'function') {
          console.warn('[VoiceAssistant] 浏览器不支持语音合成');
          // 显示提示信息
          this.$toast({
            message: '当前浏览器不支持语音播报',
            type: 'warning',
            duration: 2000
          });
          return;
        }

        // 检查文本是否有效
        if (!text || typeof text !== 'string') {
          console.warn('[VoiceAssistant] 无效的语音文本');
          return;
        }

        // 停止当前正在播放的语音
        if (typeof window.speechSynthesis.cancel === 'function') {
          window.speechSynthesis.cancel();
        }

        // 创建语音合成实例
        const utterance = new SpeechSynthesisUtterance(text);
        
        // 设置语音参数
        utterance.lang = 'zh-CN'; // 中文
        utterance.rate = 1.0; // 语速
        utterance.pitch = 1.0; // 音调
        utterance.volume = 1.0; // 音量

        // 添加事件监听
        utterance.onstart = () => {
          console.log('[VoiceAssistant] 开始播放语音:', text);
        };

        utterance.onend = () => {
          console.log('[VoiceAssistant] 语音播放完成');
        };

        utterance.onerror = (event) => {
          console.error('[VoiceAssistant] 语音播报错误:', event);
          // 某些浏览器在用户未交互时会报错，这里不显示错误提示
          if (event.error !== 'interrupted' && event.error !== 'canceled') {
            this.$toast({
              message: '语音播报失败',
              type: 'warning',
              duration: 2000
            });
          }
        };

        // 播放语音
        window.speechSynthesis.speak(utterance);

        console.log('[VoiceAssistant] 开始播放语音提示:', text);
      } catch (error) {
        console.error('[VoiceAssistant] 语音播报失败:', error);
        // 不显示错误提示，避免影响用户体验
      }
    },

    handleError(error) {
      console.error('[VoiceAssistant] Error occurred:', error);

      let errorMessage = '操作失败，请重试';

      if (error.name === 'NotAllowedError') {
        errorMessage = '请允许麦克风权限后重试';
      } else if (error.name === 'NotFoundError') {
        errorMessage = '未找到麦克风设备';
      } else if (error.name === 'NotSupportedError') {
        errorMessage = '浏览器不支持语音录制';
      } else if (error.message) {
        errorMessage = error.message;
      }

      // 上报错误到aegis
      try {
        const { aegis } = this.$store.state.user;
        if (aegis) {
          aegis.infoAll({
            msg: JSON.stringify({
              type: 'voice_assistant_error',
              errorName: error.name,
              errorMessage: errorMessage,
              error: error.message || error.toString(),
              stack: error.stack,
              timestamp: new Date().toISOString()
            }),
            ext1: 'voice-assistant-error',
            trace: 'voice-assistant-general-error',
          });
        }
      } catch (reportError) {
        console.error('[VoiceAssistant] 上报通用错误失败:', reportError);
      }

      this.statusText = errorMessage;
      this.resetRecording();

      // 显示错误提示
      this.$toast({
        message: errorMessage,
        type: 'fail',
        duration: 3000
      });
    },

    resetRecording() {
      console.log('[VoiceAssistant] resetRecording called');

      // 清理计时器
      this.clearRecordingTimers();

      // 重置状态
      this.isRecording = false;
      this.isInitializing = false; // 清除初始化状态
      this.isClickRecording = false;
      this.isProcessing = false;
      this.isCancelGesture = false;
      this.isLongPress = false;
      this.isCanSendData = false;
      this.isCanStop = false;
      
      // 清除状态文本
      this.statusText = '';

      // 清理录音器
      if (this.recorder) {
        try {
          this.recorder.stop();
        } catch (e) {
          console.warn('停止录音器时出错:', e);
        }
        this.recorder = null;
      }

      // 清理语音识别器
      if (this.speechRecognizer) {
        try {
          if (this.isCanStop) {
            this.speechRecognizer.stop();
          }
        } catch (e) {
          console.warn('停止语音识别器时出错:', e);
        }
      }

      // 清理计时器
      if (this.recordingTimer) {
        clearTimeout(this.recordingTimer);
        this.recordingTimer = null;
      }
    }
  }
};
</script>

<style scoped>
/* 小弹窗样式 */
.voice-assistant-small-modal {
  background: white;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
  padding: 0;
  text-align: center;
  border: 1px solid #ebedf0;
  backdrop-filter: blur(10px);
  animation: modalFadeIn 0.3s ease-out;
  overflow: hidden;
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
}

/* 遮罩层样式 */
.voice-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1999;
  animation: overlayFadeIn 0.3s ease-out;
}

@keyframes overlayFadeIn {
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
}

/* 全屏模式样式 - 65%高度 */
.voice-assistant-small-modal.fullscreen-mode {
  position: fixed !important;
  left: 0 !important;
  bottom: 0 !important;
  top: auto !important;
  width: 100vw !important;
  height: 65vh !important;
  border-radius: 16px 16px 0 0;
  z-index: 2000;
  display: flex;
  flex-direction: column;
  animation: slideUpFadeIn 0.3s ease-out;
  box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.15);
}

@keyframes slideUpFadeIn {
  0% {
    opacity: 0;
    transform: translateY(100%);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 16px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  position: relative;
}

.fullscreen-mode .modal-header {
  padding: 12px 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border-radius: 16px 16px 0 0;
}

.modal-header-left,
.modal-header-right {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
  min-width: 60px; /* 确保左右两侧有足够空间 */
}

.modal-header-left {
  justify-content: flex-start;
}

.modal-header-right {
  justify-content: flex-end;
}

.modal-title {
  font-size: 13px;
  font-weight: bold;
  flex: 1;
  text-align: center;
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  pointer-events: none; /* 避免遮挡按钮点击 */
}

.modal-clear-btn {
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border-radius: 50%;
  transition: all 0.2s;
  flex-shrink: 0;
  background-color: rgba(255, 255, 255, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.3);
  pointer-events: auto; /* 恢复点击 */
}

.modal-clear-btn:hover {
  background-color: rgba(255, 255, 255, 0.25);
  border-color: rgba(255, 255, 255, 0.5);
  transform: scale(1.1);
}

.modal-clear-btn:active {
  transform: scale(0.9);
}

.modal-settings-btn {
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border-radius: 50%;
  transition: all 0.2s;
  flex-shrink: 0;
  background-color: rgba(255, 255, 255, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.3);
  pointer-events: auto; /* 恢复点击 */
}

.modal-settings-btn:hover {
  background-color: rgba(255, 255, 255, 0.25);
  border-color: rgba(255, 255, 255, 0.5);
  transform: scale(1.1);
}

.modal-settings-btn:active {
  transform: scale(0.9);
}

.modal-close-btn {
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border-radius: 50%;
  transition: all 0.2s;
  flex-shrink: 0;
  background-color: rgba(255, 255, 255, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.3);
  pointer-events: auto; /* 恢复点击 */
}

.modal-close-btn:hover {
  background-color: rgba(255, 255, 255, 0.25);
  border-color: rgba(255, 255, 255, 0.5);
  transform: scale(1.1);
}

.modal-close-btn:active {
  transform: scale(0.9);
}

.permission-notice-small {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
  background: #fff7e6;
  border: 1px solid #ffd591;
  border-radius: 6px;
  margin: 12px 16px;
  font-size: 12px;
  color: #d46b08;
  gap: 4px;
}

.voice-button-container-small {
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 12px 16px;
  height: 80px;
}

.fullscreen-input .voice-button-container-small {
  margin: 0;
  height: 100px;
}

.voice-button-small {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border: none;
  color: white;
  font-size: 12px;
  font-weight: bold;
  transition: all 0.3s ease;
  z-index: 2;
  position: relative;
  cursor: pointer;
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  -webkit-touch-callout: none;
}

.fullscreen-input .voice-button-small {
  width: 70px;
  height: 70px;
  font-size: 13px;
}

.voice-button-small.recording {
  background: linear-gradient(135deg, #ff6b6b 0%, #4ecdc4 100%);
  animation: pulseSmall 1.5s infinite;
  transform: scale(1.1);
}

.voice-button-small.processing {
  animation: processingSmall 2s infinite linear;
}

.voice-button-small.api-processing {
  background: linear-gradient(135deg, #4ecdc4 0%, #44a08d 100%);
  transform: scale(1.1);
  box-shadow: 0 0 20px rgba(78, 205, 196, 0.5);
}

.voice-button-small.cancel-gesture {
  background: linear-gradient(135deg, #ff4757 0%, #ff3838 100%);
  transform: scale(0.9);
  box-shadow: 0 0 20px rgba(255, 71, 87, 0.5);
}

.voice-animation-small {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 1;
  width: 100px;
  height: 100px;
}

.fullscreen-input .voice-animation-small {
  width: 120px;
  height: 120px;
}

.pulse-circle-small {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 80px;
  height: 80px;
  border: 2px solid rgba(255, 107, 107, 0.4);
  border-radius: 50%;
  transform: translate(-50%, -50%);
  animation: pulseRingSmall 2s infinite ease-out;
}

.wave-lines-small {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 3px;
  width: 40px;
  height: 30px;
}

.wave-line-small {
  width: 3px;
  height: 6px;
  background: linear-gradient(to top, #ff6b6b, #4ecdc4);
  border-radius: 2px;
  animation: waveMotionSmall 1.2s infinite ease-in-out;
  animation-delay: calc(var(--i) * 0.15s);
  opacity: 0.8;
}

.status-text-small {
  font-size: 12px;
  color: #969799;
  margin: 8px 16px;
  min-height: 16px;
}

.fullscreen-input .status-text-small {
  display: none;
}

.recognition-result-small {
  min-height: 40px;
  padding: 8px;
  background: #f7f8fa;
  border-radius: 6px;
  margin: 8px 16px 16px 16px;
  max-height: 80px;
  overflow-y: auto;
  transition: all 0.3s ease;
}

.fullscreen-input .recognition-result-small {
  display: none;
}

/* 对话内容区域（全屏模式） */
.conversation-content-area {
  flex: 1;
  overflow-y: auto;
  background: #f7f8fa;
  padding: 16px;
  display: flex;
  flex-direction: column;
}

.conversation-messages {
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 12px;
  scroll-behavior: smooth;
  padding-bottom: 16px;
}

/* 美化滚动条 */
.conversation-messages::-webkit-scrollbar {
  width: 4px;
}

.conversation-messages::-webkit-scrollbar-track {
  background: transparent;
}

.conversation-messages::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.2);
  border-radius: 2px;
}

.conversation-messages::-webkit-scrollbar-thumb:hover {
  background: rgba(0, 0, 0, 0.3);
}

/* 空状态样式 */
.empty-conversation {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #969799;
  animation: fadeIn 0.5s ease-out;
}

.empty-icon {
  font-size: 48px;
  margin-bottom: 16px;
  opacity: 0.6;
}

.empty-text {
  font-size: 16px;
  font-weight: 500;
  margin-bottom: 8px;
  color: #646566;
}

.empty-hint {
  font-size: 13px;
  color: #969799;
}

@keyframes fadeIn {
  0% {
    opacity: 0;
    transform: translateY(-10px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
}

/* 输入区域（全屏模式下固定在底部） */
.input-area {
  background: white;
}

.input-area.fullscreen-input {
  position: sticky;
  bottom: 0;
  left: 0;
  right: 0;
  background: white;
  border-top: 1px solid #ebedf0;
  padding: 16px;
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.05);
}

/* 实时语音输入显示 */
.realtime-input-display {
  margin-bottom: 12px;
  animation: slideUp 0.3s ease-out;
}

.realtime-input-box {
  background: white;
  border: 2px solid #667eea;
  padding: 10px 14px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  animation: inputBorderPulse 1.5s infinite ease-in-out;
}

.realtime-input-text {
  font-size: 14px;
  line-height: 1.6;
  color: #323233;
  word-wrap: break-word;
  min-height: 20px;
}

@keyframes inputBorderPulse {
  0%, 100% {
    border-color: #667eea;
    box-shadow: 0 2px 8px rgba(102, 126, 234, 0.2);
  }
  50% {
    border-color: #764ba2;
    box-shadow: 0 2px 12px rgba(118, 75, 162, 0.3);
  }
}

/* 处理状态提示 */
.processing-status {
  text-align: center;
  padding: 10px 16px;
  margin-bottom: 12px;
  font-size: 13px;
  color: #1989fa;
  background: rgba(25, 137, 250, 0.1);
  border-radius: 8px;
  animation: fadeInOut 1.5s infinite ease-in-out;
  font-weight: 500;
}

@keyframes slideUp {
  0% {
    opacity: 0;
    transform: translateY(10px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
}

/* 消息项样式 */
.message-item {
  display: flex;
  width: 100%;
  animation: messageSlideIn 0.3s ease-out;
}

.message-item.user {
  justify-content: flex-end;
}

.message-item.assistant {
  justify-content: flex-start;
}

/* 消息气泡样式 */
.message-bubble {
  max-width: 75%;
  padding: 12px 16px;
  border-radius: 16px;
  word-wrap: break-word;
  word-break: break-word;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.message-item.user .message-bubble {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border-bottom-right-radius: 4px;
}

.message-item.assistant .message-bubble {
  background: white;
  color: #323233;
  border-bottom-left-radius: 4px;
  border: 1px solid #ebedf0;
}

.message-text {
  font-size: 14px;
  line-height: 1.6;
  white-space: pre-wrap;
}

/* 实时语音输入样式 */
.message-item.realtime .message-bubble {
  opacity: 0.8;
  animation: pulse 1.5s infinite ease-in-out;
}

@keyframes pulse {
  0%, 100% {
    opacity: 0.8;
  }
  50% {
    opacity: 1;
  }
}

/* 处理状态指示器 */
.processing-indicator {
  text-align: center;
  padding: 8px;
  font-size: 12px;
  color: #969799;
  animation: fadeInOut 1.5s infinite ease-in-out;
}

@keyframes fadeInOut {
  0%, 100% {
    opacity: 0.5;
  }
  50% {
    opacity: 1;
  }
}

/* 消息滑入动画 */
@keyframes messageSlideIn {
  0% {
    opacity: 0;
    transform: translateY(10px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
}

.result-text-small {
  font-size: 13px;
  color: #323233;
  line-height: 1.4;
  word-break: break-all;
}

.processing-text-small {
  font-size: 12px;
  color: #1989fa;
  font-style: italic;
}

.placeholder-text-small {
  font-size: 12px;
  color: #c8c9cc;
  font-style: italic;
}

.scrolling-examples {
  min-height: 16px;
  max-height: 48px; /* 最多显示3行 */
  overflow: hidden;
  position: relative;
  line-height: 16px;
}

.example-text {
  transition: all 0.5s ease-in-out;
  animation: textFadeIn 0.5s ease-in-out;
  word-wrap: break-word;
  word-break: break-all;
  white-space: normal;
  line-height: 16px;
}

@keyframes textFadeIn {
  0% {
    opacity: 0;
    transform: translateY(10px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
}

/* 小弹窗动画 */
@keyframes modalFadeIn {
  0% {
    opacity: 0;
    transform: translateY(10px) scale(0.95);
  }
  100% {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

@keyframes pulseSmall {

  0%,
  100% {
    transform: scale(1.1);
    box-shadow: 0 0 0 0 rgba(255, 107, 107, 0.4);
  }

  50% {
    transform: scale(1.15);
    box-shadow: 0 0 0 8px rgba(255, 107, 107, 0.1);
  }
}

@keyframes pulseRingSmall {
  0% {
    transform: translate(-50%, -50%) scale(0.8);
    opacity: 0.8;
  }

  100% {
    transform: translate(-50%, -50%) scale(1.5);
    opacity: 0;
  }
}

@keyframes waveMotionSmall {

  0%,
  100% {
    height: 6px;
    opacity: 0.6;
  }

  50% {
    height: 20px;
    opacity: 1;
  }
}

@keyframes processingSmall {
  0% {
    transform: scale(1.1) rotate(0deg);
    background: linear-gradient(135deg, #ffa726 0%, #fb8c00 100%);
  }

  100% {
    transform: scale(1.1) rotate(360deg);
    background: linear-gradient(135deg, #ffa726 0%, #fb8c00 100%);
  }
}

/* 悬浮按钮样式 */
.floating-voice-button {
  position: fixed;
  width: 36px;
  height: 36px;
  z-index: 1000;
  cursor: pointer;
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  -webkit-touch-callout: none;
  transition: transform 0.3s ease, left 0.3s ease, top 0.3s ease;
}

.floating-voice-button:hover {
  transform: scale(1.1);
}

.floating-voice-button:active {
  transform: scale(0.95);
}

.floating-button-inner {
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 20px rgba(102, 126, 234, 0.4);
  position: relative;
  overflow: hidden;
  transition: all 0.3s ease;
}

.floating-button-inner .microphone-icon {
  z-index: 1;
  filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.1));
}

.floating-button-inner::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.2) 0%, transparent 50%);
  border-radius: 50%;
}

.floating-button-ripple {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 100%;
  height: 100%;
  border: 2px solid rgba(102, 126, 234, 0.3);
  border-radius: 50%;
  transform: translate(-50%, -50%);
}

.floating-voice-button:hover .floating-button-inner {
  background: linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%);
  box-shadow: 0 6px 25px rgba(102, 126, 234, 0.6);
}

.floating-voice-button:active .floating-button-inner {
  background: linear-gradient(135deg, #4e5bc6 0%, #5e3a7e 100%);
}

/* 涟漪动画 */
@keyframes ripple {
  0% {
    transform: translate(-50%, -50%) scale(1);
    opacity: 0.8;
  }

  50% {
    transform: translate(-50%, -50%) scale(1.5);
    opacity: 0.4;
  }

  100% {
    transform: translate(-50%, -50%) scale(2);
    opacity: 0;
  }
}

/* 拖拽时的样式 */
.floating-voice-button.dragging {
  transition: none;
  transform: scale(1.1);
}

.floating-voice-button.dragging .floating-button-inner {
  box-shadow: 0 8px 30px rgba(102, 126, 234, 0.8);
}

/* 长按状态样式 */
.floating-voice-button.long-pressing {
  transform: scale(1.15);
}

.floating-voice-button.long-pressing .floating-button-inner {
  background: linear-gradient(135deg, #ff6b6b 0%, #ee5a6f 100%);
  box-shadow: 0 8px 30px rgba(255, 107, 107, 0.8);
}

/* 长按进度环 */
.long-press-progress {
  position: absolute;
  top: -4px;
  left: -4px;
  width: 44px;
  height: 44px;
  pointer-events: none;
}

.progress-ring {
  transform: rotate(-90deg);
}

.progress-ring-circle {
  stroke-dasharray: 125.66;
  stroke-dashoffset: 125.66;
  transition: stroke-dashoffset 0.016s linear;
  stroke-linecap: round;
}

/* 角落小箭头样式 */
.voice-corner-arrow {
  position: fixed;
  bottom: 0;
  right: 0;
  width: 16px;
  height: 32px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 16px 0 0 0;
  box-shadow: -2px -2px 8px rgba(102, 126, 234, 0.3);
  cursor: pointer;
  z-index: 9998;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  animation: slideInFromRight 0.5s ease;
}

.voice-corner-arrow:active {
  transform: translate(-2px, -2px);
  box-shadow: -3px -3px 12px rgba(102, 126, 234, 0.5);
}

.voice-corner-arrow .arrow-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  animation: arrowPulse 2s ease-in-out infinite;
}

.voice-corner-arrow .arrow-icon svg {
  width: 10px;
  height: 10px;
}

@keyframes arrowPulse {
  0%, 100% {
    transform: translateX(0);
    opacity: 1;
  }
  50% {
    transform: translateX(-2px);
    opacity: 0.7;
  }
}

@keyframes slideInFromRight {
  from {
    transform: translateX(100px);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}

/* 隐藏确认对话框内容样式 */
.hide-confirm-content {
  padding: 16px 0;
  text-align: left;
}

.hide-confirm-content p {
  margin: 0 0 16px 0;
  font-size: 15px;
  color: #323233;
  text-align: center;
}

.hint-box {
  background: #f7f8fa;
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 16px;
}

.hint-title {
  font-size: 14px;
  color: #646566;
  margin-bottom: 8px;
  font-weight: 500;
}

.hint-list {
  margin: 0;
  padding-left: 20px;
  font-size: 13px;
  color: #969799;
}

.hint-list li {
  margin: 4px 0;
  line-height: 1.6;
}

/* 响应式适配 */
@media (max-width: 768px) {
  .floating-voice-button {
    width: 32px;
    height: 32px;
  }
  
  .long-press-progress {
    width: 40px;
    height: 40px;
  }
  
  .progress-ring-circle {
    stroke-dasharray: 113.1;
    stroke-dashoffset: 113.1;
  }
}

/* 动画定义 */
@keyframes pulse {

  0%,
  100% {
    transform: scale(1.1);
    box-shadow: 0 0 0 0 rgba(255, 107, 107, 0.4);
  }

  50% {
    transform: scale(1.15);
    box-shadow: 0 0 0 10px rgba(255, 107, 107, 0.1);
  }
}

@keyframes pulseRing {
  0% {
    transform: translate(-50%, -50%) scale(0.8);
    opacity: 0.8;
  }

  50% {
    transform: translate(-50%, -50%) scale(1.2);
    opacity: 0.4;
  }

  100% {
    transform: translate(-50%, -50%) scale(1.8);
    opacity: 0;
  }
}

@keyframes waveMotion {

  0%,
  100% {
    height: 8px;
    opacity: 0.6;
    transform: scaleY(1);
  }

  25% {
    height: 16px;
    opacity: 0.8;
    transform: scaleY(1.2);
  }

  50% {
    height: 32px;
    opacity: 1;
    transform: scaleY(1.5);
  }

  75% {
    height: 20px;
    opacity: 0.9;
    transform: scaleY(1.3);
  }
}

@keyframes processing {
  0% {
    transform: scale(1.1) rotate(0deg);
    background: linear-gradient(135deg, #ffa726 0%, #fb8c00 100%);
  }

  50% {
    transform: scale(1.15) rotate(180deg);
    background: linear-gradient(135deg, #fb8c00 0%, #f57c00 100%);
  }

  100% {
    transform: scale(1.1) rotate(360deg);
    background: linear-gradient(135deg, #ffa726 0%, #fb8c00 100%);
  }
}

/* 唤醒词监听状态样式 */
.wake-word-status {
  margin: 8px 16px;
  padding: 8px 12px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 8px;
  animation: pulseGlow 2s infinite;
}

.wake-word-indicator {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: white;
  font-size: 12px;
  font-weight: 500;
}

.listening-dot {
  width: 8px;
  height: 8px;
  background: #4CAF50;
  border-radius: 50%;
  animation: pulse 1.5s infinite;
  box-shadow: 0 0 8px rgba(76, 175, 80, 0.8);
}

@keyframes pulse {
  0%, 100% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.3);
    opacity: 0.7;
  }
}

@keyframes pulseGlow {
  0%, 100% {
    box-shadow: 0 0 10px rgba(102, 126, 234, 0.3);
  }
  50% {
    box-shadow: 0 0 20px rgba(102, 126, 234, 0.6);
  }
}

/* 设置按钮样式 - 耳朵图标 */
.modal-settings-btn {
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border-radius: 50%;
  transition: all 0.3s ease;
  background: rgba(255, 255, 255, 0.1);
  margin-right: 8px;
  color: #fff;
}

.modal-settings-btn svg {
  transition: all 0.3s ease;
}

.modal-settings-btn:hover {
  background: rgba(255, 255, 255, 0.2);
  transform: scale(1.1);
}

.modal-settings-btn:active {
  transform: scale(0.95);
}

/* 唤醒词开启状态 */
.modal-settings-btn.wake-word-active {
  background: rgba(76, 175, 80, 0.3);
  color: #4CAF50;
  border: 1px solid rgba(76, 175, 80, 0.5);
}

.modal-settings-btn.wake-word-active:hover {
  background: rgba(76, 175, 80, 0.4);
  border-color: rgba(76, 175, 80, 0.7);
}

/* 唤醒词监听中状态 - 明显的动画效果 */
.modal-settings-btn.wake-word-listening {
  background: linear-gradient(135deg, rgba(76, 175, 80, 0.4) 0%, rgba(56, 142, 60, 0.4) 100%);
  color: #4CAF50;
  border: 2px solid #4CAF50;
  animation: listeningPulse 2s infinite ease-in-out;
  box-shadow: 0 0 15px rgba(76, 175, 80, 0.6);
}

.modal-settings-btn.wake-word-listening svg {
  animation: earWiggle 1.5s infinite ease-in-out;
}

@keyframes listeningPulse {
  0%, 100% {
    transform: scale(1);
    box-shadow: 0 0 15px rgba(76, 175, 80, 0.6);
  }
  50% {
    transform: scale(1.1);
    box-shadow: 0 0 25px rgba(76, 175, 80, 0.9);
  }
}

@keyframes earWiggle {
  0%, 100% {
    transform: rotate(0deg);
  }
  25% {
    transform: rotate(-5deg);
  }
  75% {
    transform: rotate(5deg);
  }
}
</style>